import { v4 as uuidv4 } from "uuid";
import { Box, Typography, IconButton, Button, Tooltip } from "@mui/material";
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import CopyIcon from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import React from "react";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import axios from "axios";
import { AppDispatch } from "../store/store";
import { useDispatch, useSelector } from "react-redux";
import { AGENDA } from "../constants";
import { OptionType } from "../components/common/ui/CustomAutocomplete";
import { useAuth } from "../context/AuthContext";
import { IMeetingReducerState } from "../interfaces/store/IMeetingReducerState";
import FloatingEditSummaryPopup from "./FloatingEditSummaryPopup";
import SpeakerTimestamp from "./SpeakerTimestamp";
import { actionTypes } from "../store/actionTypes/actionTypes";
import LoaderWithText from "./common/ui/LoaderWithText";
import { RestfulUrls } from "../constants/restfulUrls";
import {
  SummaryContainerProps,
  SummaryData,
  IAgenda,
  Transcription,
  AgendaOptionType,
  Speaker,
} from "../interfaces/home/IMainContainer";
import { AGENDA_DATA } from "../constants/agenda";
import AppTitleTypography from "./common/ui/AppTitleTypography";
import StandaloneSpeaker from "./StandaloneSpeaker";
import Summary from "./Summary";
import Agenda from "./Agenda";
import { COMMON } from "../constants/common";
import { hasPermission } from "../utils/permissionUtil";
import { PERMISSION } from "../constants/userPermission";
import { IUserDetailsPermissionState } from "../interfaces/store/IUserDetailsPermissionState";
import { USER } from "../constants/userCred";
import AddSpeakerSectionModal from "./AddSpeakerSectionModal";
import EditSpeakerModal from "./EditSpeakerModal";
import { AppDialogBox } from "./common/ui/AppDialogBox";
import { Button as MuiButton } from "@mui/material";

// Initialize window._env_ if it doesn't exist
if (typeof window !== 'undefined' && !window._env_) {
  window._env_ = {};
}

const baseURL = (window._env_ && window._env_.RTRT_APP_BASE_URL) || "http://localhost:8000/server/";

// Constant for add speaker section button delay window (60 seconds)
const ADD_SPEAKER_SECTION_DELAY_SECONDS = 30;

// Type guard functions (defined outside component for use in helper functions)
const isAgendaType = (item: Transcription): item is IAgenda =>
  item?.type === "agenda";

// Helper function to find correct insert index based on sequence, preserving speakers between agendas
const findInsertIndexBySequence = (list: Transcription[], targetSequence: number): number => {
  // Find the first item where sequence is greater than target
  // This works for both speakers and ensures correct positioning relative to agendas
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    if (item.type === "speaker") {
      const itemSequence = item.sequence ?? 0;
      if (itemSequence > targetSequence) {
        return i;
      }
    }
    // For agendas, we don't compare by sequence, but we continue searching
    // Speakers should be inserted based on their sequence relative to other speakers
  }
  // If no position found, return -1 to append at end
  return -1;
};

// Deduplication function: Remove speakers from standalone if they are in an agenda
// CRITICAL: Only match by speech_id, not by id, because multiple speakers can have the same id
// but different speech_id (they are separate speech instances)
const deduplicateSpeakers = (list: Transcription[]): Transcription[] => {
  if (list.length === 0) return list;

  // Collect all speech_ids that are in agendas
  // Use Map to track which agenda each speaker belongs to for better debugging
  const speakersInAgendas = new Set<string>();
  const speakerToAgendaMap = new Map<string, string>(); // speech_id -> agenda_id

  for (const item of list) {
    if (item.type === "agenda" && isAgendaType(item)) {
      const agenda = item as IAgenda;
      // CRITICAL: Ensure agenda has a valid ID
      const agendaIdentifier = agenda.id || agenda.agenda_id?.toString() || 'unknown';
      if (!agenda.id && !agenda.agenda_id) {
        console.warn(`⚠️ Found agenda without ID or agenda_id, skipping speaker tracking:`, agenda);
        continue;
      }

      if (agenda.speakers && Array.isArray(agenda.speakers)) {
        for (const speaker of agenda.speakers) {
          // CRITICAL: Only track by speech_id - this is the unique identifier for each speech
          // Multiple speakers can have the same id (same speaker entity) but different speech_id (different speeches)
          if (speaker && speaker.speech_id) {
            speakersInAgendas.add(speaker.speech_id);
            speakerToAgendaMap.set(speaker.speech_id, agendaIdentifier);
          }
        }
      }
    }
  }

  // Filter out standalone speakers that are in agendas
  // CRITICAL: Only match by speech_id to avoid false positives
  // Multiple speakers can share the same id but have different speech_id
  const deduplicated = list.filter((item) => {
    if (item.type === "agenda") {
      return true; // Keep all agendas
    }
    if (item.type === "speaker") {
      const speaker = item as Speaker;

      // CRITICAL: Only check speech_id - this is the unique identifier
      // Don't check id because multiple speeches can have the same id
      const speechIdMatch = speaker.speech_id && speakersInAgendas.has(speaker.speech_id);

      if (speechIdMatch && speaker.speech_id) {
        const agendaId = speakerToAgendaMap.get(speaker.speech_id) || 'unknown';
        console.log(`🗑️ Deduplication: Removing standalone speaker ${speaker.speech_id} (id: ${speaker.id}) - it's in agenda ${agendaId}`);
      }

      return !speechIdMatch;
    }
    return true;
  });

  if (deduplicated.length !== list.length) {
    const removedCount = list.length - deduplicated.length;
    console.log(`📋 Deduplication: Removed ${removedCount} duplicate speakers (kept ${deduplicated.length} items)`);

    // Log what was removed for debugging
    const removed = list.filter((item) => {
      if (item.type === "speaker") {
        const speaker = item as Speaker;
        return speaker.speech_id && speakersInAgendas.has(speaker.speech_id);
      }
      return false;
    });

    if (removed.length > 0) {
      console.log(`📋 Removed speakers details:`, removed.map((s: any) => ({
        speech_id: s.speech_id,
        id: s.id,
        text: s.text?.substring(0, 30)
      })));
    }
  }

  return deduplicated;
};

// CRITICAL: Deduplicate speakers within each agenda's speakers array
// This prevents duplicate keys in React when the same speech_id appears multiple times in an agenda
const deduplicateAgendaSpeakers = (list: Transcription[]): Transcription[] => {
  return list.map((item) => {
    if (item.type === "agenda" && isAgendaType(item)) {
      const agenda = item as IAgenda;
      if (agenda.speakers && Array.isArray(agenda.speakers) && agenda.speakers.length > 0) {
        // Deduplicate speakers by speech_id
        const seenSpeechIds = new Set<string>();
        const deduplicatedSpeakers: Speaker[] = [];
        let duplicatesRemoved = 0;

        for (const speaker of agenda.speakers) {
          if (speaker.speech_id) {
            if (!seenSpeechIds.has(speaker.speech_id)) {
              deduplicatedSpeakers.push(speaker);
              seenSpeechIds.add(speaker.speech_id);
            } else {
              duplicatesRemoved++;
              console.warn(`⚠️ Removing duplicate speaker ${speaker.speech_id} from agenda ${agenda.id || agenda.agenda_id} (speakers array deduplication)`);
            }
          } else {
            // If no speech_id, include it (shouldn't happen, but handle gracefully)
            deduplicatedSpeakers.push(speaker);
          }
        }

        if (duplicatesRemoved > 0) {
          console.log(`🔧 Deduplicated agenda ${agenda.id || agenda.agenda_id}: removed ${duplicatesRemoved} duplicate speakers (kept ${deduplicatedSpeakers.length} unique)`);
          return {
            ...agenda,
            speakers: deduplicatedSpeakers
          } as IAgenda;
        }
      }
    }
    return item;
  });
};

// Helper function to check if a speaker is the last in a consecutive group of same speaker_id
// AND if 60 seconds have passed since the speaker's timestamp
// This is used to determine if we should show "Add speaker section" button
const shouldShowActionButtonsAfterSpeakerTranscribed = (
  transcriptions: Transcription[],
  currentIndex: number,
  currentTime: Date
): boolean => {
  const currentItem = transcriptions[currentIndex];

  // Only check speakers, not agendas
  if (!currentItem || currentItem.type !== "speaker") {
    return true; // Show button for non-speakers (agendas)
  }

  const currentSpeaker = currentItem as Speaker;

  // If manually added, always show button
  if (currentSpeaker.is_manually_added) {
    return true;
  }

  // Check if next item is a speaker with same speaker_id
  const nextIndex = currentIndex + 1;
  if (nextIndex >= transcriptions.length) {
    // This is the last item, check if 60 seconds have passed
    return checkTimeDelay(currentSpeaker.timestamp, currentTime);
  }

  const nextItem = transcriptions[nextIndex];
  if (nextItem.type !== "speaker") {
    // Next item is an agenda, so this is the last speaker before agenda
    // Check if 60 seconds have passed
    return checkTimeDelay(currentSpeaker.timestamp, currentTime);
  }

  const nextSpeaker = nextItem as Speaker;

  // If next speaker has different speaker_id or is manually added, this is the last in group
  // Check if 60 seconds have passed
  if (nextSpeaker.speaker_id !== currentSpeaker.speaker_id || nextSpeaker.is_manually_added) {
    return checkTimeDelay(currentSpeaker.timestamp, currentTime);
  }

  // Next speaker has same speaker_id and is not manually added - hide button
  return false;
};

// Helper function to check if 60 seconds (or more) have passed since the timestamp
const checkTimeDelay = (timestamp: string | undefined, currentTime: Date): boolean => {
  if (!timestamp) {
    return true; // If no timestamp, show button (fallback)
  }

  try {
    const speakerTime = new Date(timestamp);
    // Convert both dates to UTC before calculating time difference
    // getTime() returns UTC milliseconds, but we ensure both dates are explicitly treated as UTC
    const speakerTimeUTC = new Date(speakerTime.toISOString());
    const currentTimeUTC = new Date(currentTime.toISOString());
    const timeDifferenceSeconds = Math.abs((currentTimeUTC.getTime() - speakerTimeUTC.getTime()) / 1000);

    // Show button if 60 seconds or more have passed (greater than or equal to 60 seconds)
    return timeDifferenceSeconds >= ADD_SPEAKER_SECTION_DELAY_SECONDS;
  } catch (e) {
    console.warn(`⚠️ Could not parse timestamp: ${timestamp}`, e);
    return true; // If parsing fails, show button (fallback)
  }
};

// Simple sort: Just sort by sequence number - sequence is the source of truth
// No complex logic, no reordering - just use sequence numbers as they are
const stableAgendaSort = (list: Transcription[]): Transcription[] => {
  if (list.length === 0) return list;

  // First deduplicate to remove speakers that are in agendas
  const deduplicated = deduplicateSpeakers(list);

  // CRITICAL: Deduplicate speakers within each agenda's speakers array
  // This prevents duplicate keys in React
  const agendaDeduplicated = deduplicateAgendaSpeakers(deduplicated);

  // Simple approach: sort everything by sequence number
  // Speakers have sequence numbers, agendas use __fixedOrderIndex (which is also a sequence)
  return [...agendaDeduplicated].sort((a, b) => {
    let aSeq: number;
    let bSeq: number;

    if (a.type === "speaker") {
      aSeq = a.sequence ?? 0;
    } else if (a.type === "agenda") {
      // Agendas use __fixedOrderIndex which is set to the speaker's sequence when created
      aSeq = (a as any).__fixedOrderIndex ?? a.sequence ?? 0;
    } else {
      aSeq = 0;
    }

    if (b.type === "speaker") {
      bSeq = b.sequence ?? 0;
    } else if (b.type === "agenda") {
      // Agendas use __fixedOrderIndex which is set to the speaker's sequence when created
      bSeq = (b as any).__fixedOrderIndex ?? b.sequence ?? 0;
    } else {
      bSeq = 0;
    }

    // Simple numeric sort - sequence is the source of truth
    return aSeq - bSeq;
  });
};

const MainContainer: React.FC<SummaryContainerProps> = ({
  setLoading,
  showRetryModal,
}) => {
  const { userRole } = useAuth();

  const dispatch = useDispatch<AppDispatch>();

  const fetchTranscribeId = useSelector(
    (state: { meeting: IMeetingReducerState }) =>
      state.meeting.fetchTranscribeId
  );
  const userPermissions = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.rolePermission
  );

  const [mergedMeetingId, setMergedMeetingId] = useState<string>();

  const joinMeetingAgendaStreamStartedRef = useRef(false); // To ensure startAgendaStream is triggered only once
  const { id: meetingDbId } = useParams<{ id: string }>();
  const { meetingId: uniqueMeetingId } = useParams<{ meetingId: string }>();
  const [renderedSpeakers] = useState<Set<number>>(new Set());
  const [startIndex, setStartIndex] = useState<any>();
  const [agendaStart, setAgendaStart] = useState<boolean>(false);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [isUnderlined, setIsUnderlined] = useState(false);
  const [currentAgenda, setCurrentAgenda] = useState<IAgenda | null>(null);
  const [countryOptions, setCountryOptions] = useState<OptionType[]>([]);
  const [selectedValue, setSelectedValue] = useState<any>();
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [editSummModalOpen, setEditSummModalOpen] = useState(false);
  const [editSummIndex, setEditSummIndex] = useState(null);
  const [editSummAgendaIndex, setEditSummAgendaIndex] = useState(null);
  const [editSummary, setEditSummary] = useState("");
  const [editSummaryTitle, setEditSummaryTitle] = useState("");
  const [editSummaryType, setEditSummaryType] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [apiLoadingCount, setApiLoadingCount] = useState(0);
  const [navigateSpeechId, setNavigateSpeechId] = useState("");

  // Helper functions to manage API loading counter
  const startApiCall = () => {
    setApiLoadingCount((prev) => prev + 1);
  };

  const endApiCall = () => {
    setApiLoadingCount((prev) => Math.max(0, prev - 1));
  };
  const agendaRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const startRecordingEventSource = useRef<EventSource | null>(null);
  const agendaStreamEventSource = useRef<EventSource | null>(null);
  const [isAgendaBroadcasting, setIsAgendaBroadcasting] = useState(false);

  // Add Speaker Section state and refs
  const [addSpeakerModalOpen, setAddSpeakerModalOpen] = useState<boolean>(false);
  const [addSpeakerAfterIndex, setAddSpeakerAfterIndex] = useState<number | null>(null);
  const [isAddingSpeaker, setIsAddingSpeaker] = useState(false);
  // Track current time for checking if 60 seconds have passed since speaker timestamp
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  // Store agenda context when adding speaker inside agenda
  const [addSpeakerAgendaContext, setAddSpeakerAgendaContext] = useState<{
    agendaId: string | null;
    agendaIndex: number;
    speakerIndex: number;
    speaker: Speaker;
  } | null>(null);

  // Delete Intervention Summary state
  const [deleteInterventionSummaryConfirmOpen, setDeleteInterventionSummaryConfirmOpen] = useState(false);
  const [deletingInterventionSummary, setDeletingInterventionSummary] = useState<{
    speechId: string;
    index: number;
    agendaIndex?: number | null;
  } | null>(null);
  const [isDeletingInterventionSummary, setIsDeletingInterventionSummary] = useState(false);
  const [editSpeakerModalOpen, setEditSpeakerModalOpen] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState<Speaker | null>(null);
  const [isEditingSpeaker, setIsEditingSpeaker] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [connectionLostOpen, setConnectionLostOpen] = useState(false);
  const [deletingSpeaker, setDeletingSpeaker] = useState<Speaker | null>(null);
  const [isDeletingSpeaker, setIsDeletingSpeaker] = useState(false);


  // Pending insertion tracking
  type PendingInsertion = {
    afterSpeechId: string;
    afterIndex: number;
    tempSpeechId: string;
    tempId: number; // temporary id for matching
    timeoutId: number;
    backendSpeechId?: string; // Backend authoritative speech_id for matching
    backendSequence?: number; // Backend sequence for insertion
    insertAfterSequence?: number; // Sequence to insert after
    isInAgenda?: boolean; // Whether speaker is being added inside an agenda
    agendaId?: string | null | undefined; // Agenda ID if speaker is in agenda
    location?: string; // Location: "standalone" or "agenda:{agenda_id}"
    speakerData?: { // Speaker data for manual insertion if SSE doesn't arrive
      speaker_id: string;
      text: string;
      duration: string;
      timestamp: string;
    };
  };
  const pendingInsertionsMapRef = useRef<Map<string, PendingInsertion>>(new Map());

  // Track deleted speakers to prevent them from being re-added by delayed SSE messages
  const deletedSpeakersRef = useRef<Set<string>>(new Set());

  // Ref to access latest transcriptions state in callbacks/timeouts
  const transcriptionsRef = useRef<Transcription[]>(transcriptions);

  const activeEventSources: Map<string, EventSource> = new Map();

  // Initialize data when meetingId changes
  useEffect(() => {
    if (uniqueMeetingId) {
      const mergedId = uniqueMeetingId + "_" + meetingDbId;
      setMergedMeetingId(mergedId);
      const storedStartIndex = localStorage.getItem(
        `startIndex_${mergedId}`
      );
      const storedCurrentAgenda = localStorage.getItem(
        `currentAgenda_${mergedId}`
      );
      const storedAgendaStart = localStorage.getItem(
        `agendaStart_${mergedId}`
      );
      const storedTranscriptions = localStorage.getItem(
        `transcriptions_${mergedId}`
      );

      if (mergedId) {
        dispatch({
          type: actionTypes.UPDATE_MEETING_ID,
          payload: mergedId,
        });
      }

      if (storedStartIndex) {
        setStartIndex(storedStartIndex);
      }

      if (storedCurrentAgenda) {
        setCurrentAgenda(JSON.parse(storedCurrentAgenda));
      }

      if (storedTranscriptions) {
        const parsedTranscriptions = JSON.parse(storedTranscriptions);
        // Deduplicate and sort transcriptions loaded from localStorage
        const cleanedTranscriptions = stableAgendaSort(parsedTranscriptions);
        setTranscriptions(cleanedTranscriptions);
      }

      if (storedAgendaStart) {
        setAgendaStart(JSON.parse(storedAgendaStart));
      }
      fetchCountries();
    }
  }, [uniqueMeetingId, meetingDbId]); // Run when meetingId or meetingDbId changes

  // NOTE:: handleNavigateSpeechIdmethod use for getting id and set to state and after 1.5 sec reset so navigated highlited transcription goes automatically after 1.5 seconds
  const handleNavigateSpeechId = (id: string) => {
    setNavigateSpeechId(id);
    // Reset state after 1.5 seconds
    setTimeout(() => {
      setNavigateSpeechId("");
    }, 1500);
  };

  const handleError = (e: Event, id: string | undefined, eventSource: EventSource | null) => {
    // Only log errors when the connection is actually closed or in a bad state
    // EventSource can fire error events during reconnection attempts even when working
    if (eventSource && eventSource.readyState === EventSource.CLOSED) {
      console.log('EventSource connection closed:', e, id);
      setLoading(false);
    }
    // Ignore errors when readyState is CONNECTING (0) or OPEN (1) - these are normal
  };

  useEffect(() => {
    setMergedMeetingId(uniqueMeetingId + "_" + meetingDbId);
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [showRetryModal, uniqueMeetingId]);

  // Keep transcriptionsRef in sync with transcriptions state
  useEffect(() => {
    transcriptionsRef.current = transcriptions;
  }, [transcriptions]);

  const isAgenda = (item: Transcription): item is IAgenda =>
    item?.type === "agenda";

  const isSpeaker = useCallback((item: Transcription): item is Speaker => {
    return item && item?.type && item.type === "speaker";
  }, []);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const postAPiCall = async (
    url: string,
    payload: any,
    callback?: (response: any) => void
  ) => {
    startApiCall();
    try {
      const response = await axios.post(url, payload);
      if (callback) {
        callback(response.data);
      }

      // // Check if this is an agenda update call and if agenda stream is empty
      // if (url.includes('agenda-detail/update') && !agendaStreamEventSource.current) {
      //   startAgendaStream(uniqueMeetingId);
      // }
    } catch (error: any) {
      console.error("API Error:", {
        url,
        payload,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      setIsAgendaBroadcasting(false);

      // If it's a 404, log the full URL for debugging
      if (error.response?.status === 404) {
        console.error(`404 Not Found - URL: ${url}`);
      }

      // If it's a 500, log the error details
      if (error.response?.status === 500) {
        console.error(`500 Internal Server Error - URL: ${url}`, error.response?.data);
      }
    } finally {
      endApiCall();
    }
  };

  // Update current time every second to check if 60 seconds have passed for add speaker button
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (fetchTranscribeId) {
      setShowLoader(true);
      startStream(uniqueMeetingId, false);
      getMeetingAgendaDetails();
    }
  }, [fetchTranscribeId]);

  const startAgendaStream = (meetingId: string | undefined) => {
    const url = `${baseURL}realtime-streaming/stream/agenda/${meetingId}/${meetingDbId}`;
    const key = `agendaStream-${meetingId}_${meetingDbId}`;

    if (activeEventSources.has(key)) {
      return;
    }

    if (!navigator.onLine) return;

    console.log(`Starting agenda stream: ${url}`);
    agendaStreamEventSource.current = new EventSource(url); // Azure STT


    agendaStreamEventSource.current.onmessage = (event) => {
      setShowLoader(false);
      const data = JSON.parse(event.data);

      // Handle ADD_SPEAKER_TO_AGENDA type messages
      if (data && data.type === "ADD_SPEAKER_TO_AGENDA") {
        const updatedAgenda = data.agenda;
        const speechId = data.speech_id;
        const speakerIndex = data.speaker_index;
        const isManuallyAdded = data.is_manually_added;

        if (!updatedAgenda) {
          console.warn("ADD_SPEAKER_TO_AGENDA message missing agenda, ignoring", data);
          return;
        }

        const agendaId = updatedAgenda.id || updatedAgenda.agenda_id?.toString();

        if (!agendaId) {
          console.warn("ADD_SPEAKER_TO_AGENDA message missing agenda id, ignoring", data);
          return;
        }

        console.log(`➕ Processing ADD_SPEAKER_TO_AGENDA event for agenda: ${agendaId}`, {
          speech_id: speechId,
          speaker_index: speakerIndex,
          is_manually_added: isManuallyAdded,
          total_speakers: updatedAgenda.speakers?.length || 0,
          agenda_id_from_event: updatedAgenda.id || updatedAgenda.agenda_id,
          full_event_data: data
        });

        setTranscriptions((prevTranscriptions: any) => {
          const updated = [...prevTranscriptions];

          // Debug: Log all agendas in current state
          const allAgendas = updated.filter((item) => item.type === "agenda");
          console.log(`🔍 Current agendas in state (${allAgendas.length} total):`,
            allAgendas.map((a: any) => ({
              id: a.id,
              agenda_id: a.agenda_id,
              type: a.type
            }))
          );

          // CRITICAL FIX: Remove any existing standalone speaker with the same speech_id
          // This prevents speakers from appearing both in agenda and as standalone
          const standaloneIndex = updated.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (standaloneIndex !== -1) {
            updated.splice(standaloneIndex, 1);
            console.log(`🗑️ Removed standalone speaker ${speechId} before adding to agenda (was duplicate)`);
          }

          // Find and update the agenda by ID
          const agendaIndex = updated.findIndex(
            (item) => item.type === "agenda" && (
              item.id === agendaId ||
              (isAgenda(item) && item.agenda_id?.toString() === agendaId) ||
              item.id?.toString() === agendaId?.toString() ||
              (isAgenda(item) && item.agenda_id?.toString() === agendaId?.toString())
            )
          );

          console.log(`🔍 Looking for agenda with id=${agendaId}, found index=${agendaIndex}`);

          if (agendaIndex !== -1) {
            const existingAgenda = updated[agendaIndex] as IAgenda;

            // CRITICAL: Start with a fresh copy of existing speakers to avoid mutation issues
            const existingSpeakers = existingAgenda.speakers || [];

            // CRITICAL: Check if speaker already exists (from optimistic update) to prevent duplicates
            // If speaker exists with same speech_id, update it instead of replacing the entire agenda
            const existingSpeakerIndex = existingSpeakers.findIndex(
              (s: Speaker) => s.speech_id === speechId
            );

            if (existingSpeakerIndex !== undefined && existingSpeakerIndex !== -1) {
              // Speaker already exists (from optimistic update) - update it with complete data from backend
              const backendSpeaker = updatedAgenda.speakers?.find((s: Speaker) => s.speech_id === speechId);

              if (backendSpeaker) {
                // CRITICAL FIX: Use backend's speakers array directly to preserve correct order and sequence
                // The backend has the authoritative order based on insert_position_in_agenda
                // We should use it directly instead of trying to merge, which can cause sequence mismatches
                const backendSpeakers = updatedAgenda.speakers || [];

                // Update the existing speaker with backend data (preserve any local state if needed)
                const updatedBackendSpeakers = backendSpeakers.map((s: Speaker) => {
                  if (s.speech_id === speechId) {
                    return {
                      ...s,
                      ...backendSpeaker,
                      synced: true, // Mark as synced now that we have data from backend
                    };
                  }
                  return s;
                });

                console.log(`✅ Updated existing optimistic speaker ${speechId} with backend data, using backend's speakers array (${updatedBackendSpeakers.length} speakers) to preserve correct order`);

                // Merge the updated agenda data while preserving local state and using backend's speakers array
                updated[agendaIndex] = {
                  ...existingAgenda,
                  ...updatedAgenda,
                  speakers: updatedBackendSpeakers, // Use backend's authoritative speakers array with updated speaker
                  // Preserve local UI state
                  isExpanded: existingAgenda.isExpanded,
                  // Preserve fixed order index for proper sorting
                  __fixedOrderIndex: (existingAgenda as any).__fixedOrderIndex || updatedAgenda.sequence,
                } as IAgenda & { __fixedOrderIndex?: number };
              } else {
                // Backend speaker not found, but we have optimistic one - keep it
                console.warn(`⚠️ Backend speaker ${speechId} not found in updatedAgenda.speakers, keeping optimistic version`);
                updated[agendaIndex] = {
                  ...existingAgenda,
                  ...updatedAgenda,
                  speakers: existingSpeakers, // Keep existing speakers
                  isExpanded: existingAgenda.isExpanded,
                  __fixedOrderIndex: (existingAgenda as any).__fixedOrderIndex || updatedAgenda.sequence,
                } as IAgenda & { __fixedOrderIndex?: number };
              }
            } else {
              // Speaker doesn't exist yet - use backend's speakers array directly
              // CRITICAL: Backend sends the complete, correctly ordered speakers array
              // We should use it directly to ensure proper sequence matching across browsers
              console.log(`✅ Adding new speaker ${speechId} from ADD_SPEAKER_TO_AGENDA event (no optimistic update found)`);

              // CRITICAL FIX: Use backend's speakers array directly to preserve correct order and sequence
              // The backend has the authoritative order based on insert_position_in_agenda
              const backendSpeakers = updatedAgenda.speakers || [];

              // Only merge if we have existing speakers that aren't in backend (shouldn't happen, but safety check)
              const existingSpeakers = existingAgenda.speakers || [];
              const backendSpeechIds = new Set(backendSpeakers.map((s: Speaker) => s.speech_id).filter(Boolean));

              // Check if there are any existing speakers not in backend (shouldn't happen for agenda speakers)
              const missingFromBackend = existingSpeakers.filter((s: Speaker) =>
                s.speech_id && !backendSpeechIds.has(s.speech_id)
              );

              if (missingFromBackend.length > 0) {
                console.warn(`⚠️ Found ${missingFromBackend.length} existing speakers not in backend array - this shouldn't happen for agenda speakers`);
                // In this case, merge but prioritize backend order
                const finalSpeakers: Speaker[] = [];
                const seenSpeechIds = new Set<string>();

                // First, add all backend speakers (authoritative order)
                for (const backendSpeaker of backendSpeakers) {
                  if (backendSpeaker.speech_id) {
                    if (!seenSpeechIds.has(backendSpeaker.speech_id)) {
                      finalSpeakers.push(backendSpeaker);
                      seenSpeechIds.add(backendSpeaker.speech_id);
                    }
                  } else {
                    finalSpeakers.push(backendSpeaker);
                  }
                }

                // Then add any missing speakers (shouldn't happen, but safety)
                for (const missingSpeaker of missingFromBackend) {
                  if (missingSpeaker.speech_id && !seenSpeechIds.has(missingSpeaker.speech_id)) {
                    finalSpeakers.push(missingSpeaker);
                    seenSpeechIds.add(missingSpeaker.speech_id);
                  }
                }

                updated[agendaIndex] = {
                  ...existingAgenda,
                  ...updatedAgenda,
                  speakers: finalSpeakers,
                  isExpanded: existingAgenda.isExpanded,
                  __fixedOrderIndex: (existingAgenda as any).__fixedOrderIndex || updatedAgenda.sequence,
                } as IAgenda & { __fixedOrderIndex?: number };
              } else {
                // Normal case: Use backend's speakers array directly (correct order and sequence)
                updated[agendaIndex] = {
                  ...existingAgenda,
                  ...updatedAgenda,
                  speakers: backendSpeakers, // Use backend's authoritative speakers array
                  // Preserve local UI state
                  isExpanded: existingAgenda.isExpanded,
                  // Preserve fixed order index for proper sorting
                  __fixedOrderIndex: (existingAgenda as any).__fixedOrderIndex || updatedAgenda.sequence,
                } as IAgenda & { __fixedOrderIndex?: number };

                console.log(`✅ Using backend's speakers array directly (${backendSpeakers.length} speakers) to preserve correct order and sequence`);
              }
            }

            console.log(`✅ Updated agenda ${agendaId} with ${updated[agendaIndex].speakers?.length || 0} speakers from ADD_SPEAKER_TO_AGENDA event`);

            // Note: Regenerate button is always visible for admin/super admin
            if (agendaId) {
              console.log(`➕ Speaker added to agenda ${agendaId} via ADD_SPEAKER_TO_AGENDA`);
            }

            return stableAgendaSort(updated);
          } else {
            console.warn(`⚠️ ADD_SPEAKER_TO_AGENDA: Agenda with id ${agendaId} not found in transcriptions`);
            return prevTranscriptions;
          }
        });

        return; // Exit early for ADD_SPEAKER_TO_AGENDA messages
      }

      // Handle DELETE_INTERVENTION_SUMMARY type messages
      if (data && data.type === "DELETE_INTERVENTION_SUMMARY") {
        const speechId = data.speech_id;

        if (!speechId) {
          console.warn("DELETE_INTERVENTION_SUMMARY message missing speech_id, ignoring", data);
          return;
        }

        console.log(`🗑️ Processing DELETE_INTERVENTION_SUMMARY event for speech_id: ${speechId}`);

        setTranscriptions((prevTranscriptions: any) => {
          const updated = [...prevTranscriptions];
          let updatedAny = false;

          // Update standalone speaker if it exists
          const standaloneIndex = updated.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (standaloneIndex !== -1) {
            const speaker = updated[standaloneIndex] as Speaker;
            updated[standaloneIndex] = {
              ...speaker,
              summary: "",
              country: "",
              intervention_label_id: undefined,
              intervention_label_name: "",
              summaryTimestamp: undefined,
              createdBy: "",
            };
            updatedAny = true;
            console.log(` Cleared intervention summary for standalone speaker ${speechId}`);
          }

          // Update speaker in agendas if it exists
          updated.forEach((item, index) => {
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              if (agenda.speakers) {
                const speakerIndex = agenda.speakers.findIndex(
                  (s: Speaker) => s.speech_id === speechId
                );

                if (speakerIndex !== -1) {
                  const updatedSpeakers = [...agenda.speakers];
                  updatedSpeakers[speakerIndex] = {
                    ...updatedSpeakers[speakerIndex],
                    summary: "",
                    country: "",
                    intervention_label_id: undefined,
                    intervention_label_name: "",
                    summaryTimestamp: undefined,
                    createdBy: "",
                  };
                  updated[index] = {
                    ...agenda,
                    speakers: updatedSpeakers,
                  } as IAgenda;
                  updatedAny = true;
                  console.log(` Cleared intervention summary for speaker ${speechId} in agenda ${agenda.id}`);
                }
              }
            }
          });

          if (updatedAny) {
            return stableAgendaSort(updated);
          }

          return prevTranscriptions;
        });

        return; // Exit early for DELETE_INTERVENTION_SUMMARY messages
      }

      // Handle UPDATE_INTERVENTION_SUMMARY type messages (when intervention summary is updated)
      // This can come as a SPEAKER message with updated summary fields, but we also handle explicit events
      if (data && (data.type === "UPDATE_INTERVENTION_SUMMARY" || data.type === "INTERVENTION_TEXT_UPDATED")) {
        const speechId = data.speech_id || data.speaker?.speech_id;

        if (!speechId) {
          console.warn("UPDATE_INTERVENTION_SUMMARY/INTERVENTION_TEXT_UPDATED message missing speech_id, ignoring", data);
          return;
        }

        console.log(` Processing ${data.type} event for speech_id: ${speechId}`);

        // CRITICAL: Extract speaker update data, excluding needs_intervention_regeneration
        // If data.speaker exists, use it; otherwise build from event properties
        let speakerUpdateData: any = {};
        if (data.speaker) {
          // If speaker object exists, use it but exclude needs_intervention_regeneration
          const { needs_intervention_regeneration, needs_regeneration, ...cleanSpeaker } = data.speaker;
          speakerUpdateData = cleanSpeaker;
        } else {
          // If no speaker object, extract only relevant properties from event
          // Exclude event-specific properties like type, needs_intervention_regeneration, old_summary_visible
          if (data.new_text !== undefined) {
            speakerUpdateData.text = data.new_text;
          }
          if (data.speaker_id !== undefined) {
            speakerUpdateData.speaker_id = data.speaker_id;
          }
          // Include any other speaker properties that might be in the event
          // but exclude: type, needs_intervention_regeneration, old_summary_visible
        }

        setTranscriptions((prevTranscriptions: any) => {
          const updated = [...prevTranscriptions];
          let updatedAny = false;

          // Update standalone speaker if it exists
          const standaloneIndex = updated.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (standaloneIndex !== -1) {
            const existingSpeaker = updated[standaloneIndex] as Speaker;
            updated[standaloneIndex] = {
              ...existingSpeaker,
              ...speakerUpdateData,
              // Preserve sequence
              sequence: existingSpeaker.sequence || speakerUpdateData.sequence,
            };
            updatedAny = true;
            console.log(` Updated intervention summary for standalone speaker ${speechId}`);
          }

          // Update speaker in agendas if it exists
          updated.forEach((item, index) => {
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              if (agenda.speakers) {
                const speakerIndex = agenda.speakers.findIndex(
                  (s: Speaker) => s.speech_id === speechId
                );

                if (speakerIndex !== -1) {
                  const updatedSpeakers = [...agenda.speakers];
                  updatedSpeakers[speakerIndex] = {
                    ...updatedSpeakers[speakerIndex],
                    ...speakerUpdateData,
                    // Preserve sequence
                    sequence: updatedSpeakers[speakerIndex].sequence || speakerUpdateData.sequence,
                  };
                  updated[index] = {
                    ...agenda,
                    speakers: updatedSpeakers,
                  } as IAgenda;
                  updatedAny = true;
                  console.log(` Updated intervention summary for speaker ${speechId} in agenda ${agenda.id}`);
                }
              }
            }
          });

          if (updatedAny) {
            return stableAgendaSort(updated);
          }

          return prevTranscriptions;
        });

        return; // Exit early for UPDATE_INTERVENTION_SUMMARY/INTERVENTION_TEXT_UPDATED messages
      }

      // Handle DELETE_SPEAKER type messages (from agenda stream)
      if (data && data.type === "DELETE_SPEAKER") {
        const speechId = data.speech_id;

        if (!speechId) {
          console.warn("DELETE_SPEAKER message missing speech_id, ignoring", data);
          return;
        }

        console.log(` Processing DELETE_SPEAKER event (from agenda stream) for speech_id: ${speechId}`);

        // Track deleted speaker to prevent re-adding from delayed SSE messages
        deletedSpeakersRef.current.add(speechId);

        setTranscriptions((prevTranscriptions: any) => {
          const updated = [...prevTranscriptions];
          let speakerRemoved = false;

          // Remove standalone speaker
          const standaloneIndex = updated.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (standaloneIndex !== -1) {
            updated.splice(standaloneIndex, 1);
            speakerRemoved = true;
            console.log(` Removed standalone speaker with speech_id ${speechId} (from agenda stream)`);
          }

          // Also remove from any agendas that contain this speaker
          updated.forEach((item, index) => {
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              if (agenda.speakers && agenda.speakers.some(s => s.speech_id === speechId)) {
                const updatedSpeakers = agenda.speakers.filter(s => s.speech_id !== speechId);
                updated[index] = {
                  ...agenda,
                  speakers: updatedSpeakers,
                } as IAgenda;
                speakerRemoved = true;

                // Note: Regenerate button is always visible for admin/super admin
                if (agenda.id) {
                  console.log(` Speaker deleted from agenda ${agenda.id} via agenda stream`);
                }

                console.log(` Removed speaker ${speechId} from agenda ${agenda.id} (from agenda stream)`);
              }
            }
          });

          if (speakerRemoved) {
            console.log(` Removed speaker with speech_id ${speechId} from transcriptions (from agenda stream)`);
            return stableAgendaSort(updated);
          } else {
            console.warn(` DELETE_SPEAKER: Speaker with speech_id ${speechId} not found in transcriptions (from agenda stream)`);
            return prevTranscriptions;
          }
        });

        return; // Exit early for DELETE_SPEAKER messages
      }

      // Handle UPDATE_AGENDA_ON_DELETE_SPEAKER type messages
      if (data && data.type === "UPDATE_AGENDA_ON_DELETE_SPEAKER") {
        const agendaId = data.agenda_id;
        const updatedAgenda = data.agenda;

        if (!agendaId) {
          console.warn("UPDATE_AGENDA_ON_DELETE_SPEAKER message missing agenda_id, ignoring", data);
          return;
        }

        if (!updatedAgenda) {
          console.warn("UPDATE_AGENDA_ON_DELETE_SPEAKER message missing agenda, ignoring", data);
          return;
        }

        console.log(`🔄 Processing UPDATE_AGENDA_ON_DELETE_SPEAKER event for agenda: ${agendaId}`);

        setTranscriptions((prevTranscriptions: any) => {
          const updated = [...prevTranscriptions];

          // Find the agenda by ID
          const agendaIndex = updated.findIndex(
            (item) => item.type === "agenda" && (
              item.id === agendaId ||
              (isAgenda(item) && item.agenda_id?.toString() === agendaId) ||
              item.id?.toString() === agendaId?.toString() ||
              (isAgenda(item) && item.agenda_id?.toString() === agendaId?.toString())
            )
          );

          if (agendaIndex !== -1) {
            const existingAgenda = updated[agendaIndex] as IAgenda;

            // Update only summary and speakers properties
            updated[agendaIndex] = {
              ...existingAgenda,
              summary: updatedAgenda.summary,
              speakers: updatedAgenda.speakers || [],
              // Preserve local UI state
              isExpanded: existingAgenda.isExpanded,
              // Preserve fixed order index for proper sorting
              __fixedOrderIndex: (existingAgenda as any).__fixedOrderIndex,
            } as IAgenda & { __fixedOrderIndex?: number };

            console.log(`✅ Updated agenda ${agendaId} with new summary and ${updatedAgenda.speakers?.length || 0} speakers from UPDATE_AGENDA_ON_DELETE_SPEAKER event`);

            return stableAgendaSort(updated);
          } else {
            console.warn(`⚠️ UPDATE_AGENDA_ON_DELETE_SPEAKER: Agenda with id ${agendaId} not found in transcriptions`);
            return prevTranscriptions;
          }
        });

        return; // Exit early for UPDATE_AGENDA_ON_DELETE_SPEAKER messages
      }

      if (data && data.type === "AGENDA") {
        const agenda = data.agenda;
        const startIndex = data.startIndex;

        // CRITICAL: Preserve agenda_id from backend if present in data
        // Backend sends: { agenda_id: "uuid", agenda: { agenda_id: number, ... } }
        // The agenda.agenda_id is the database agenda_id (number) - THIS IS THE REAL ONE FROM REDIS
        if (isAgenda(agenda) && agenda.agenda_id) {
          console.log(` Received agenda from backend with agenda_id: ${agenda.agenda_id} (id: ${agenda.id})`);
        }

        if (
          agenda.status &&
          ((agenda.status.initial === AGENDA_DATA.AGENDA_STATUS.RESUME &&
            agenda.status.final === "") ||
            (agenda.status.initial === AGENDA_DATA.AGENDA_STATUS.START &&
              agenda.status.final === ""))
        ) {
          // Functional update to ensure you use the latest transcriptions state
          setTranscriptions((prevTranscriptions: any) => {
            let agendaPrevious = { ...prevTranscriptions[startIndex] };
            if (agendaPrevious && agendaPrevious?.id === agenda.id) {
              const copyTranscriptions = [...prevTranscriptions];
              // CRITICAL: Preserve agenda_id from backend if it exists (this is the real one from Redis)
              if (isAgenda(agenda) && agenda.agenda_id) {
                (copyTranscriptions[startIndex] as IAgenda).agenda_id = agenda.agenda_id;
                console.log(` Preserved backend agenda_id ${agenda.agenda_id} for agenda ${agenda.id}`);
              }
              // CRITICAL: Ensure speakers array is preserved from the updated agenda
              // The updated agenda from Redis should have the complete speakers array including manually added ones
              const existingAgenda = copyTranscriptions[startIndex] as IAgenda;
              if (isAgenda(agenda) && agenda.speakers && Array.isArray(agenda.speakers) && agenda.speakers.length > 0) {
                // Use the speakers array from the updated agenda (which includes manually added speakers from Redis)
                console.log(` Using agenda speakers from Redis: ${agenda.speakers.length} speakers for agenda ${agenda.id}`);
              } else if (existingAgenda.speakers && Array.isArray(existingAgenda.speakers) && existingAgenda.speakers.length > 0) {
                // If updated agenda doesn't have speakers, preserve existing ones (fallback)
                agenda.speakers = existingAgenda.speakers;
                console.log(` Preserving existing speakers: ${existingAgenda.speakers.length} speakers for agenda ${agenda.id}`);
              } else {
                // Initialize empty speakers array if neither has speakers
                if (!agenda.speakers) {
                  agenda.speakers = [];
                }
                console.log(` Initializing empty speakers array for agenda ${agenda.id}`);
              }
              // Store fixed order index before sorting
              if (!(agenda as any).__fixedOrderIndex) {
                (agenda as any).__fixedOrderIndex = startIndex;
              }
              copyTranscriptions[startIndex] = agenda;
              return stableAgendaSort(copyTranscriptions);
            } else {
              // Store fixed order index before sorting
              if (!(agenda as any).__fixedOrderIndex) {
                (agenda as any).__fixedOrderIndex = startIndex;
              }
              const updatedTranscriptions = [
                ...prevTranscriptions.slice(0, startIndex),
                agenda,
                ...prevTranscriptions.slice(startIndex),
              ];
              return stableAgendaSort(updatedTranscriptions);
            }
          });
        } else if (
          agenda.status &&
          agenda.status.initial !== "" &&
          agenda.status.final !== ""
        ) {
          //clearLocalStorage();
          setTranscriptions((prevTranscriptions: any) => {
            //Note::It end index will be start index + 1 due to it is single obj
            // CRITICAL: agenda.agenda_id from backend is the real one from Redis - preserve it
            const updatedTranscriptions = handleFinalStateAgendaStreaming(
              startIndex + 1,
              agenda,
              startIndex,
              prevTranscriptions
            );
            const transcriptionList: any = updatedTranscriptions?.filter(
              (item) => item.type === "agenda" || item.type === "speaker"
            );
            return stableAgendaSort(transcriptionList);
          });

        }
      } else if (data && data.type === "SPEAKER") {
        setTranscriptions((prevTranscriptions: any) => {
          //Note::It end index will be start index + 1 due to it is single obj
          const speaker = data.speaker;
          //const startIndex = data.startIndex;

          if (!speaker || !speaker.speech_id) {
            console.warn("SPEAKER message missing speaker or speech_id, ignoring", data);
            return prevTranscriptions;
          }

          // Check if this is a deleted speaker (should not process)
          if (deletedSpeakersRef.current.has(speaker.speech_id)) {
            console.log(`🛡️ Agenda stream: Ignoring SPEAKER event for deleted speaker ${speaker.speech_id}`);
            return prevTranscriptions;
          }

          const updated = [...prevTranscriptions];
          let updatedAny = false;

          // CRITICAL: Check if speaker is already in an agenda - if so, update it there instead of standalone
          let speakerInAgenda = false;
          for (let i = 0; i < updated.length; i++) {
            const item = updated[i];
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              if (agenda.speakers) {
                const speakerIndex = agenda.speakers.findIndex(
                  (s: Speaker) => s.speech_id === speaker.speech_id
                );

                if (speakerIndex !== -1) {
                  // Speaker exists in this agenda - update it
                  const updatedSpeakers = [...agenda.speakers];
                  // Merge existing speaker data with new data (preserve local state if needed)
                  updatedSpeakers[speakerIndex] = {
                    ...updatedSpeakers[speakerIndex],
                    ...speaker,
                    // Preserve sequence if it exists
                    sequence: updatedSpeakers[speakerIndex].sequence || speaker.sequence,
                  };
                  updated[i] = {
                    ...agenda,
                    speakers: updatedSpeakers,
                  } as IAgenda;
                  updatedAny = true;
                  speakerInAgenda = true;
                  console.log(` Updated speaker ${speaker.speech_id} in agenda ${agenda.id} (intervention summary may have been updated)`);
                  break;
                }
              }
            }
          }

          // If speaker is not in an agenda, update/add as standalone
          if (!speakerInAgenda) {
            // Match by speech_id to ensure intervention summaries are preserved correctly
            // speech_id is the persistent identifier used for intervention summaries
            const speakerIndex = updated.findIndex(
              (ele: Transcription) => isSpeaker(ele) && ele.speech_id === speaker.speech_id
            );

            if (speakerIndex !== -1) {
              // Update existing standalone speaker (merge to preserve any local state)
              const existingSpeaker = updated[speakerIndex] as Speaker;
              updated[speakerIndex] = {
                ...existingSpeaker,
                ...speaker,
                // Preserve sequence if it exists
                sequence: existingSpeaker.sequence || speaker.sequence,
              };
              updatedAny = true;
              console.log(` Updated standalone speaker ${speaker.speech_id} (intervention summary may have been updated)`);
            } else {
              // If speaker not found, add it (for new speakers) - but only if not in an agenda
              // Check if this is a manually added speaker (should be added)
              if (speaker.is_manually_added || speaker.status === "transcribed") {
                updated.push(speaker);
                updatedAny = true;
                console.log(` Added new standalone speaker ${speaker.speech_id} from agenda stream`);
              } else {
                console.log(` Skipping non-transcribed speaker ${speaker.speech_id} from agenda stream`);
              }
            }
          }

          if (updatedAny) {
            const transcriptionList: any = updated?.filter(
              (item: Transcription) =>
                item.type === "agenda" || item.type === "speaker"
            );
            return stableAgendaSort(transcriptionList);
          }

          return prevTranscriptions;
        });
      }
    };

    agendaStreamEventSource.current.onerror = (e) => {
      // Only handle errors when the connection is actually closed
      // EventSource fires error events during reconnection attempts, which is normal
      console.error(`Agenda stream connection closed or connecting: ${url}`);
      setConnectionLostOpen(true);
      if (agendaStreamEventSource.current?.readyState === EventSource.CLOSED || agendaStreamEventSource.current?.readyState === EventSource.CONNECTING) {
        if (agendaStreamEventSource.current) {
          agendaStreamEventSource.current.close();
        }
        if (startRecordingEventSource.current) {
          startRecordingEventSource.current.close();
        }
      }
      handleError(e, meetingId, agendaStreamEventSource.current);
      setLoading(false);
      // Ignore errors when readyState is CONNECTING (0) or OPEN (1) - connection is still working
    };
  };

  const clearLocalStorage = () => {
    setTimeout(() => {
      const openAgendas = transcriptions?.filter(
        (item: Transcription) => item.type === "agenda" && (item.status.initial !== "" && item.status.final == "")
      );
      if (openAgendas?.length == 0) {
        setCurrentAgenda(null);
        setStartIndex(0);
        localStorage.setItem(
          `startIndex_${mergedMeetingId}`,
          JSON.stringify(null)
        );
        setAgendaStart(false);
        localStorage.setItem(`agendaStart_${mergedMeetingId}`, "false");
        localStorage.setItem(`currentAgenda_${mergedMeetingId}`, JSON.stringify(null));
      } else {
        localStorage.setItem(
          `startIndex_${mergedMeetingId}`,
          JSON.stringify(openAgendas[0].id)
        );
        setStartIndex(openAgendas[0].id);
        setAgendaStart(true);
        setCurrentAgenda(isAgenda(openAgendas[0]) ? openAgendas[0] : null);
        localStorage.setItem(`agendaStart_${mergedMeetingId}`, JSON.stringify(true));
        localStorage.setItem(`currentAgenda_${mergedMeetingId}`, JSON.stringify(openAgendas[0]));
        localStorage.setItem(`agendaStart_${mergedMeetingId}`, JSON.stringify(true));
      }
    }, 100);
  }

  const handleFinalStateAgendaStreaming = (
    endIndex: number,
    agenda: IAgenda,
    start_index: number,
    transcriptionsState: Transcription[] // Pass the latest transcriptions state
  ) => {
    if (start_index === -1 || endIndex === -1 || start_index >= endIndex) {
      return; // Return the unchanged state if conditions are not met
    }

    if (isAgenda(agenda)) {
      // Find agenda by id (agenda position is maintained by stable sort)
      const index = transcriptionsState.findIndex(
        (transcription) => transcription.id === agenda.id
      );

      if (index !== -1) {
        // If the transcription exists, update it
        const updatedTranscriptions = [...transcriptionsState]; // Copy the array
        const transcriptionToUpdate = updatedTranscriptions[index];

        // CRITICAL: Only match by speech_id - this is the unique identifier for each speech
        // Multiple speakers can have the same id (same speaker entity) but different speech_id (different speeches)
        const speechIdsToRemove = (agenda.speakers || []).map(
          (speaker: any) => speaker.speech_id
        ).filter(id => id != null);

        // CRITICAL FIX: Remove speakers that are in the agenda's speakers array
        // Only match by speech_id to avoid false positives
        // Speakers that are NOT in the agenda remain standalone (whether manually added or not)
        const filteredTranscriptions = updatedTranscriptions.filter(
          (item) => {
            if (item.type === "agenda") {
              return true; // Keep all agendas
            }
            if (item.type === "speaker") {
              const speaker = item as Speaker;
              // Remove speakers that are in the agenda's speakers array
              // CRITICAL: Only match by speech_id - multiple speeches can share the same id
              const shouldRemove = speaker.speech_id && speechIdsToRemove.includes(speaker.speech_id);
              if (shouldRemove) {
                console.log(` Removing speaker from standalone transcriptions (included in agenda): ${speaker.speech_id} (${speaker.text?.substring(0, 30)})`);
              }
              return !shouldRemove; // Keep speakers that are NOT in the agenda
            }
            return true;
          }
        );

        console.log(` handleFinalStateAgendaStreaming: Removed ${updatedTranscriptions.length - filteredTranscriptions.length} speakers from standalone (they're in agenda)`);

        // Recalculate index after filtering
        const newIndex = filteredTranscriptions.findIndex(
          (transcription) => transcription.id === agenda.id
        );

        // Preserve fixed order index
        if (!(agenda as any).__fixedOrderIndex && isAgenda(transcriptionToUpdate) && (transcriptionToUpdate as any).__fixedOrderIndex !== undefined) {
          (agenda as any).__fixedOrderIndex = (transcriptionToUpdate as any).__fixedOrderIndex;
        } else if (!(agenda as any).__fixedOrderIndex) {
          (agenda as any).__fixedOrderIndex = start_index;
        }

        // Merge agenda into the specific index
        // CRITICAL: Preserve agenda_id from backend (this is the real one from Redis)
        if (newIndex !== -1) {
          const mergedAgenda = {
            ...transcriptionToUpdate,
            ...agenda,
          };
          // Ensure backend agenda_id is preserved (this is the database ID from Redis)
          if (isAgenda(agenda) && agenda.agenda_id) {
            (mergedAgenda as IAgenda).agenda_id = agenda.agenda_id;
            console.log(` Preserved backend agenda_id ${agenda.agenda_id} in handleFinalStateAgendaStreaming`);
          }
          filteredTranscriptions[newIndex] = mergedAgenda;
        }
        return [...filteredTranscriptions]; // Return the updated array
      } else {
        // If the transcription does not exist, update the state with the new agenda
        // Store fixed order index before inserting
        if (!(agenda as any).__fixedOrderIndex) {
          (agenda as any).__fixedOrderIndex = start_index;
        }

        // CRITICAL: Remove speakers from standalone that are in this agenda
        // Only match by speech_id - multiple speeches can share the same id
        const speechIdsToRemove = (agenda.speakers || []).map(
          (speaker: any) => speaker.speech_id
        ).filter(id => id != null);

        // Filter out speakers that are in the agenda
        const filteredState = transcriptionsState.filter((item) => {
          if (item.type === "agenda") {
            return true; // Keep all agendas
          }
          if (item.type === "speaker") {
            const speaker = item as Speaker;
            // CRITICAL: Only match by speech_id - multiple speeches can share the same id
            const shouldRemove = speaker.speech_id && speechIdsToRemove.includes(speaker.speech_id);
            if (shouldRemove) {
              console.log(` Removing speaker from standalone (included in new agenda): ${speaker.speech_id}`);
            }
            return !shouldRemove;
          }
          return true;
        });

        // Find the correct insertion index after filtering
        const insertIndex = Math.min(start_index, filteredState.length);

        const updatedTranscriptions = [
          ...filteredState.slice(0, insertIndex),
          agenda,
          ...filteredState.slice(insertIndex),
        ];
        return [...updatedTranscriptions]; // Return the updated array
      }
    }

    return [...transcriptionsState]; // Return the unchanged state if no agenda is found
  };

  const fetchCountries = async () => {
    startApiCall();
    try {
      const response = await axios.get(
        `${RestfulUrls.BASE_URL}${RestfulUrls.INTERVENTION_LABEL_LIST}`
      );

      const options = response.data.map((label: any) => ({
        label: label.label,
        value: label.id,
        tag: label.tag,
        order: label.order,
        isActive: label.isActive,
        id: label.id,
      }));

      setCountryOptions(options);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      endApiCall();
    }
  };

  // ========== Add Speaker Section Helper Functions ==========
  /**
   * Get available speaker IDs from transcriptions
   */
  const getAvailableSpeakerIds = (): string[] => {
    const speakerIds = new Set<string>();
    transcriptions.forEach((item) => {
      if (isSpeaker(item)) {
        speakerIds.add(String(item.speaker_id));
      } else if (isAgenda(item) && item.speakers) {
        item.speakers.forEach((speaker) => {
          speakerIds.add(String(speaker.speaker_id));
        });
      }
    });
    return Array.from(speakerIds).sort();
  };

  /**
   * Main handler for adding speaker section (waits for backend/SSE - no optimistic update)
   */
  const handleAddSpeakerSection = async (data: {
    speaker_id: string;
    text: string;
    duration: string;
    timestamp?: string;
  }) => {
    if (!mergedMeetingId || addSpeakerAfterIndex === null) {
      throw new Error("Meeting ID or target index is required");
    }

    // CRITICAL: Check if we're adding inside an agenda (speaker doesn't exist as standalone)
    if (addSpeakerAgendaContext) {
      const { agendaId: contextAgendaId, speakerIndex, speaker } = addSpeakerAgendaContext;
      if (!contextAgendaId) {
        throw new Error("Agenda ID is required");
      }
      const agendaId: string = contextAgendaId; // Type narrowing after null check

      if (!speaker.speech_id) {
        throw new Error("Speaker speech_id is required");
      }

      const speakerSpeechId: string = speaker.speech_id; // Type narrowing
      console.log(` Adding speaker inside agenda ${agendaId} after speaker index ${speakerIndex} (will insert at position ${speakerIndex + 1})`);

      // Find the agenda to get its sequence
      const agenda = transcriptions.find(
        (item) => item.type === "agenda" &&
          (item.id === agendaId || (isAgenda(item) && item.agenda_id?.toString() === agendaId))
      ) as IAgenda | undefined;

      if (!agenda) {
        throw new Error(`Agenda ${agendaId} not found`);
      }

      // CRITICAL: Use the sequence from the speaker at the insertion position in the agenda
      // The speaker in agenda.speakers[speakerIndex] should have a sequence property
      // This is the most accurate - use the sequence from the speaker we're inserting after
      let insert_after_sequence: number | undefined = undefined;

      // PRIORITY 1: Use sequence directly from the speaker at insertion position in agenda
      if (agenda.speakers && Array.isArray(agenda.speakers) && speakerIndex >= 0 && speakerIndex < agenda.speakers.length) {
        const targetSpeaker = agenda.speakers[speakerIndex];
        if (targetSpeaker) {
          // First, check if the speaker has a sequence property directly
          if (targetSpeaker.sequence != null && typeof targetSpeaker.sequence === 'number') {
            insert_after_sequence = targetSpeaker.sequence;
            console.log(`Using sequence directly from agenda speaker at index ${speakerIndex}: ${insert_after_sequence}`);
          }
          // If not, try to find it as standalone speaker in transcriptions
          else if (targetSpeaker.speech_id) {
            const targetStandalone = transcriptions.find(
              (item) => item.type === "speaker" &&
                isSpeaker(item) &&
                item.speech_id === targetSpeaker.speech_id &&
                item.sequence != null
            ) as Speaker | undefined;

            if (targetStandalone && targetStandalone.sequence != null) {
              insert_after_sequence = targetStandalone.sequence;
              console.log(` Found speaker at index ${speakerIndex} as standalone with sequence ${insert_after_sequence}`);
            }
          }
        }
      }

      // PRIORITY 2: If still not found, try to find ANY speaker in the agenda that has a sequence
      if (insert_after_sequence === undefined && agenda.speakers && Array.isArray(agenda.speakers)) {
        for (let i = 0; i < agenda.speakers.length; i++) {
          const agendaSpeaker = agenda.speakers[i];
          if (agendaSpeaker) {
            // First check if speaker has sequence directly
            if (agendaSpeaker.sequence != null && typeof agendaSpeaker.sequence === 'number') {
              insert_after_sequence = agendaSpeaker.sequence;
              console.log(` Using sequence from agenda speaker at index ${i}: ${insert_after_sequence}`);
              break;
            }
            // If not, try to find as standalone
            else if (agendaSpeaker.speech_id) {
              const standaloneSpeaker = transcriptions.find(
                (item) => item.type === "speaker" &&
                  isSpeaker(item) &&
                  item.speech_id === agendaSpeaker.speech_id &&
                  item.sequence != null
              ) as Speaker | undefined;

              if (standaloneSpeaker && standaloneSpeaker.sequence != null) {
                insert_after_sequence = standaloneSpeaker.sequence;
                console.log(` Found agenda speaker at index ${i} as standalone with sequence ${insert_after_sequence}`);
                break;
              }
            }
          }
        }
      }

      // PRIORITY 3: Last resort - use agenda's sequence or throw error
      if (insert_after_sequence === undefined) {
        if (agenda.sequence != null) {
          insert_after_sequence = agenda.sequence;
          console.log(`⚠️ Using agenda's sequence as fallback: ${insert_after_sequence}`);
        } else {
          throw new Error(`Could not find a valid sequence from agenda "${agenda.title}". Please ensure the agenda has speakers with sequences.`);
        }
      }

      // Log what sequence we're using for debugging
      console.log(`📊 Final insert_after_sequence: ${insert_after_sequence}, from agenda: ${agenda.title}, speaker index: ${speakerIndex}, speakers count: ${agenda.speakers?.length || 0}`);

      // Log the agenda ID being used for debugging
      // CRITICAL: agendaId should be the top-level UUID (agenda.id), not the nested numeric one
      if (!agenda.id) {
        console.error(`❌ ERROR: Agenda missing top-level UUID id! Using fallback: ${agendaId}`, agenda);
      } else {
        console.log(`✅ Using agenda_id: ${agendaId} (UUID from agenda.id - top level)`);
      }
      console.log(`📋 Agenda details:`, {
        agendaId: agendaId,
        agendaIdFromId: agenda.id,  // Top-level UUID - THIS IS WHAT WE SEND
        agendaIdFromAgenda: agenda.agenda_id?.toString(),  // Nested numeric - NOT USED (for reference only)
        agendaTitle: agenda.title,
        speakersInAgenda: agenda.speakers?.length || 0
      });

      // Register pending insertion
      const timeoutId = window.setTimeout(() => {
        console.warn(`⏰ Timeout: Speaker insertion not confirmed by backend after 10 seconds`);
        const pending = pendingInsertionsMapRef.current.get(`pending-${speakerSpeechId}`);
        if (pending) {
          // If this is an agenda speaker and ADD_SPEAKER_TO_AGENDA event didn't arrive, manually add it
          if (pending.isInAgenda && pending.agendaId && pending.backendSpeechId) {
            console.warn(`⚠️ ADD_SPEAKER_TO_AGENDA event didn't arrive for agenda ${pending.agendaId}, manually adding speaker`);
            setTranscriptions((prev) => {
              const updated = [...prev];
              const agendaIndex = updated.findIndex(
                (item) => item.type === "agenda" && (
                  item.id === pending.agendaId ||
                  (isAgenda(item) && item.agenda_id?.toString() === pending.agendaId)
                )
              );

              if (agendaIndex !== -1 && pending.speakerData) {
                const agenda = updated[agendaIndex] as IAgenda;
                const newSpeaker: Speaker = {
                  id: Date.now(),
                  createdBy: userRole || USER.USER_ROLE.ADMIN,
                  type: "speaker",
                  speaker_id: pending.speakerData.speaker_id,
                  duration: pending.speakerData.duration,
                  timestamp: pending.speakerData.timestamp,
                  text: pending.speakerData.text,
                  speech_id: pending.backendSpeechId,
                  sequence: (pending.backendSequence ?? pending.insertAfterSequence ?? 0) as number,
                  status: "transcribed",
                  is_manually_added: true,
                  synced: true,
                };

                if (!agenda.speakers) {
                  agenda.speakers = [];
                }

                // Insert at the correct position
                const insertIndex = speakerIndex + 1;
                agenda.speakers.splice(insertIndex, 0, newSpeaker);

                updated[agendaIndex] = { ...agenda };
                console.log(`✅ Manually added speaker ${pending.backendSpeechId} to agenda ${pending.agendaId} at index ${insertIndex}`);

                // Note: Regenerate button is always visible for admin/super admin
                if (pending.agendaId) {
                  console.log(`➕ Speaker added to agenda ${pending.agendaId} via fallback`);
                }

                return stableAgendaSort(updated);
              }

              return prev;
            });
          }

          pendingInsertionsMapRef.current.delete(`pending-${speakerSpeechId}`);
        }
      }, 10000);

      pendingInsertionsMapRef.current.set(`pending-${speakerSpeechId}`, {
        afterSpeechId: speakerSpeechId,
        afterIndex: -1, // Not applicable for agenda speakers
        tempSpeechId: `pending-${speakerSpeechId}`,
        tempId: 0,
        timeoutId,
        insertAfterSequence: insert_after_sequence,
        isInAgenda: true,
        agendaId: agendaId,
        speakerData: {
          speaker_id: data.speaker_id,
          text: data.text,
          duration: data.duration || "00:00",
          timestamp: data.timestamp || new Date().toISOString(),
        },
      });

      setIsAddingSpeaker(true);
      startApiCall();

      // Store response speech_id for error cleanup
      let responseSpeechId: string | undefined = undefined;

      try {
        const url = `${baseURL}speaker/${mergedMeetingId}/add`;

        // Build request payload with agenda context
        // CRITICAL: insert_position_in_agenda should be the actual insertion position (not the speaker index)
        // When clicking "add speaker section" below speaker at index 0, we want to insert at index 1
        // So insert_position_in_agenda = speakerIndex + 1 (the position where we insert)
        const insertPositionInAgenda = speakerIndex + 1;

        // CRITICAL: Ensure insert_after_sequence is always a float, not an integer
        const insertAfterSequenceFloat = insert_after_sequence != null
          ? (Number.isInteger(insert_after_sequence) ? parseFloat(insert_after_sequence.toFixed(1)) : parseFloat(insert_after_sequence.toString()))
          : undefined;

        const requestPayload: any = {
          insert_after_sequence: insertAfterSequenceFloat,
          speaker_id: data.speaker_id,
          text: data.text,
          duration: data.duration || "00:00",
          timestamp: data.timestamp || new Date().toISOString(),
          // CRITICAL: Pass agenda_id - backend matches by either id or agenda_id
          // Backend will match: current_agenda_id == target_agenda_id (string comparison)
          // Prefer agenda_id (database ID) as it's the real one from Redis
          agenda_id: agendaId,
          insert_position_in_agenda: insertPositionInAgenda, // Actual insertion position (speakerIndex + 1)
        };

        console.log(`📤 Sending add speaker request:`, {
          agenda_id: agendaId,
          agenda_id_type: agenda.id ? 'id (UUID - top level)' : 'agenda_id (nested numeric)',
          agenda_id_from_id: agenda.id,  // Top-level UUID - THIS IS WHAT WE SEND
          agenda_id_from_agenda: agenda.agenda_id?.toString(),  // Nested numeric - NOT USED
          insert_after_sequence: insertAfterSequenceFloat, // Any valid sequence (backend requirement)
          insert_position_in_agenda: insertPositionInAgenda, // Actual insertion position (speakerIndex + 1)
          speaker_index_clicked: speakerIndex, // The speaker index that was clicked (for reference)
          target_speaker_speech_id: speakerSpeechId,
          agenda_title: agenda.title
        });

        const response = await axios.post<{
          success: boolean;
          sequence?: number;
          message: string;
          speech_id: string;
          location: string;
          agenda_id?: string;
          speaker_index?: number;
          total_speakers?: number;
        }>(
          url,
          requestPayload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(COMMON.ACCESS_TOKEN)}`,
            },
          }
        );

        console.log(`✅ API Success - Speaker added to agenda at index ${response.data.speaker_index}`);

        if (response.data?.speech_id) {
          responseSpeechId = response.data.speech_id;
          for (const [, pendingInsertion] of Array.from(pendingInsertionsMapRef.current.entries())) {
            if (pendingInsertion.afterSpeechId === speakerSpeechId) {
              pendingInsertion.backendSpeechId = response.data.speech_id;
              pendingInsertion.backendSequence = response.data.sequence;
              pendingInsertion.location = response.data.location;

              // CRITICAL: When adding inside an agenda, we ALWAYS mark it as agenda speaker
              // Even if backend returns "standalone" (which shouldn't happen), we know it's in agenda
              if (pendingInsertion.isInAgenda) {
                // Already marked as agenda speaker, ensure agendaId is set
                if (!pendingInsertion.agendaId) {
                  pendingInsertion.agendaId = response.data.agenda_id || agendaId;
                }
                console.log(`✅ Pending insertion already marked as agenda speaker (agenda_id: ${pendingInsertion.agendaId})`);
              } else {
                // Check if response indicates speaker was added to agenda
                // Backend returns location: "agenda:{agenda_id}" when added to agenda
                const isInAgendaFromResponse = response.data.location?.startsWith('agenda:') || response.data.agenda_id;
                if (isInAgendaFromResponse) {
                  // Update pending insertion to mark it as agenda speaker
                  pendingInsertion.isInAgenda = true;
                  pendingInsertion.agendaId = response.data.agenda_id || agendaId;
                  console.log(`✅ Marked pending insertion as agenda speaker (location: ${response.data.location}, agenda_id: ${response.data.agenda_id || agendaId})`);
                } else {
                  // Backend returned "standalone" but we sent agenda_id - this is a backend issue
                  // Still mark it as agenda speaker since we know it should be in agenda
                  console.warn(`⚠️ Backend returned location: ${response.data.location} but we sent agenda_id: ${agendaId}. Marking as agenda speaker anyway.`);
                  pendingInsertion.isInAgenda = true;
                  pendingInsertion.agendaId = agendaId;
                }
              }

              console.log(`✅ Stored backend speech_id ${response.data.speech_id} for SSE reconciliation (location: ${response.data.location}, agenda_id: ${agendaId}, isInAgenda: ${pendingInsertion.isInAgenda})`);
              break;
            }
          }

          // OPTIMISTIC UPDATE: Add speaker to agenda immediately (before SSE event)
          // This provides instant feedback to the user
          setTranscriptions((prev) => {
            const updated = [...prev];
            const agendaIndex = updated.findIndex(
              (item) => item.type === "agenda" && (
                item.id === agendaId ||
                (isAgenda(item) && item.agenda_id?.toString() === agendaId)
              )
            );

            if (agendaIndex !== -1) {
              const existingAgenda = updated[agendaIndex] as IAgenda;

              // CRITICAL: Create a new speakers array to avoid mutation issues
              const currentSpeakers = existingAgenda.speakers || [];

              // Check if speaker already exists (prevent duplicates)
              const speakerExists = currentSpeakers.some(
                (s: Speaker) => s.speech_id === responseSpeechId
              );

              if (!speakerExists && responseSpeechId) {
                const newSpeaker: Speaker = {
                  id: Date.now(),
                  createdBy: userRole || USER.USER_ROLE.ADMIN,
                  type: "speaker",
                  speaker_id: data.speaker_id,
                  duration: data.duration || "00:00",
                  timestamp: data.timestamp || new Date().toISOString(),
                  text: data.text,
                  speech_id: responseSpeechId,
                  sequence: response.data.sequence || insert_after_sequence || 0,
                  status: "transcribed",
                  is_manually_added: true,
                  synced: false, // Mark as not synced yet - will be updated when SSE arrives
                };

                // CRITICAL: Create a new array instead of mutating
                const newSpeakers = [...currentSpeakers];

                // Insert at the correct position (insertPositionInAgenda)
                const insertIndex = Math.min(insertPositionInAgenda, newSpeakers.length);
                newSpeakers.splice(insertIndex, 0, newSpeaker);

                // CRITICAL: Final deduplication pass to ensure no duplicates (safety check)
                const deduplicatedSpeakers: Speaker[] = [];
                const seenSpeechIds = new Set<string>();
                for (const speaker of newSpeakers) {
                  if (speaker.speech_id) {
                    if (!seenSpeechIds.has(speaker.speech_id)) {
                      deduplicatedSpeakers.push(speaker);
                      seenSpeechIds.add(speaker.speech_id);
                    } else {
                      console.warn(`⚠️ Removing duplicate speaker ${speaker.speech_id} in optimistic update deduplication`);
                    }
                  } else {
                    deduplicatedSpeakers.push(speaker);
                  }
                }

                updated[agendaIndex] = {
                  ...existingAgenda,
                  speakers: deduplicatedSpeakers
                };
                console.log(`✅ Optimistic update: Added speaker ${responseSpeechId} to agenda ${agendaId} at index ${insertIndex} (total: ${deduplicatedSpeakers.length})`);

                // Note: Regenerate button is always visible for admin/super admin
                if (agendaId) {
                  console.log(`➕ Speaker added to agenda ${agendaId} via optimistic update`);
                }

                return stableAgendaSort(updated);
              } else {
                if (speakerExists) {
                  console.log(`⚠️ Speaker ${responseSpeechId} already exists in agenda, skipping optimistic update`);
                } else {
                  console.warn(`⚠️ Cannot add optimistic speaker: responseSpeechId is ${responseSpeechId}`);
                }
              }
            }

            return prev;
          });

          // If speaker was added to agenda, wait for ADD_SPEAKER_TO_AGENDA event to sync data
          // Check both the response and the pending insertion (in case backend returned standalone incorrectly)
          const isInAgenda = response.data.location?.startsWith('agenda:') ||
            response.data.agenda_id ||
            (pendingInsertionsMapRef.current.get(`pending-${speakerSpeechId}`)?.isInAgenda);

          if (isInAgenda) {
            console.log(`📌 Speaker added to agenda - waiting for ADD_SPEAKER_TO_AGENDA event to sync (speech_id: ${response.data.speech_id})`);
          } else {
            console.log(`📌 Waiting for SSE event to add speaker with speech_id: ${response.data.speech_id}`);
          }
        }
      } catch (error: any) {
        console.error("Error adding speaker section:", error);

        // Remove optimistic update on error (if we had added one)
        if (responseSpeechId) {
          setTranscriptions((prev) => {
            const updated = [...prev];
            const agendaIndex = updated.findIndex(
              (item) => item.type === "agenda" && (
                item.id === agendaId ||
                (isAgenda(item) && item.agenda_id?.toString() === agendaId)
              )
            );

            if (agendaIndex !== -1) {
              const existingAgenda = updated[agendaIndex] as IAgenda;
              if (existingAgenda.speakers) {
                const speakerIndex = existingAgenda.speakers.findIndex(
                  (s: Speaker) => s.speech_id === responseSpeechId
                );
                if (speakerIndex !== -1) {
                  existingAgenda.speakers.splice(speakerIndex, 1);
                  updated[agendaIndex] = { ...existingAgenda };
                  console.log(`🗑️ Removed optimistic speaker ${responseSpeechId} due to error`);
                  return stableAgendaSort(updated);
                }
              }
            }
            return prev;
          });
        }

        for (const [pendingKey, pending] of Array.from(pendingInsertionsMapRef.current.entries())) {
          if (pending.afterSpeechId === speakerSpeechId) {
            clearTimeout(pending.timeoutId);
            pendingInsertionsMapRef.current.delete(pendingKey);
            break;
          }
        }

        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to add speaker section";
        throw new Error(errorMessage);
      } finally {
        setIsAddingSpeaker(false);
        setAddSpeakerAgendaContext(null); // Clear agenda context
        endApiCall();
      }

      return; // Exit early for agenda context
    }

    // Normal flow: speaker exists as standalone in transcriptions
    const actualIndex = addSpeakerAfterIndex;
    const afterSpeaker = transcriptions[actualIndex];

    if (!afterSpeaker || !isSpeaker(afterSpeaker) || !afterSpeaker.speech_id) {
      throw new Error("Target speaker not found");
    }

    const insert_after_sequence = afterSpeaker.sequence;

    // CRITICAL: Detect if speaker is inside an agenda
    // Check if speaker has in_agenda flag or find which agenda contains it
    let targetAgenda: IAgenda | null = null;
    let insertPositionInAgenda: number | null = null;
    let agendaId: string | null = null;

    // Method 1: Check if speaker has in_agenda flag (from backend)
    if ((afterSpeaker as any).in_agenda && (afterSpeaker as any).agenda_id) {
      agendaId = (afterSpeaker as any).agenda_id;
      // Find the agenda
      targetAgenda = transcriptions.find(
        (item) => item.type === "agenda" &&
          (item.id === agendaId || (isAgenda(item) && item.agenda_id?.toString() === agendaId))
      ) as IAgenda | undefined || null;
    }

    // Method 2: Search all agendas to find which one contains this speaker
    if (!targetAgenda) {
      for (const item of transcriptions) {
        if (item.type === "agenda" && isAgenda(item)) {
          const agenda = item as IAgenda;
          if (agenda.speakers && Array.isArray(agenda.speakers)) {
            const speakerIndex = agenda.speakers.findIndex(
              (s: Speaker) => s.speech_id === afterSpeaker.speech_id
            );
            if (speakerIndex !== -1) {
              targetAgenda = agenda;
              insertPositionInAgenda = speakerIndex; // Insert after this position
              agendaId = agenda.id || agenda.agenda_id?.toString() || null;
              break;
            }
          }
        }
      }
    } else {
      // If agenda found via method 1, find speaker position
      if (targetAgenda.speakers && Array.isArray(targetAgenda.speakers)) {
        const speakerIndex = targetAgenda.speakers.findIndex(
          (s: Speaker) => s.speech_id === afterSpeaker.speech_id
        );
        if (speakerIndex !== -1) {
          insertPositionInAgenda = speakerIndex;
        }
      }
    }

    // Debug logging
    console.log("📍 ADD SPEAKER REQUEST:", {
      addSpeakerAfterIndex: actualIndex,
      afterSpeaker: afterSpeaker.speech_id,
      afterSequence: afterSpeaker.sequence,
      inAgenda: !!targetAgenda,
      agendaId: agendaId,
      insertPositionInAgenda: insertPositionInAgenda,
      agendaTitle: targetAgenda?.title || "none",
      transcriptionsLength: transcriptions.length
    });

    // Guard: Reject insertion if sequence is missing
    if (insert_after_sequence == null || insert_after_sequence === undefined) {
      const errorMsg = "Speaker not fully loaded. Please wait for sequence information.";
      console.error(errorMsg, { afterSpeaker, addSpeakerAfterIndex });
      alert(errorMsg); // Using alert as fallback, replace with toast if available
      throw new Error(errorMsg);
    }

    // Register pending insertion for SSE reconciliation with fallback mechanism
    const timeoutId = window.setTimeout(() => {
      console.warn(`⏰ Timeout: Speaker insertion not confirmed by backend after 10 seconds`);
      const pending = pendingInsertionsMapRef.current.get(`pending-${afterSpeaker.speech_id}`);
      if (pending && pending.backendSpeechId) {
        // If we have backend speech_id but SSE didn't arrive, manually add it as fallback
        // This ensures the speaker appears even if SSE is delayed or fails
        if (pending.isInAgenda && pending.agendaId) {
          // Agenda speaker - handled by existing logic
          console.warn(`⚠️ ADD_SPEAKER_TO_AGENDA event didn't arrive for agenda ${pending.agendaId}, manually adding speaker`);
          setTranscriptions((prev) => {
            const updated = [...prev];
            const agendaIndex = updated.findIndex(
              (item) => item.type === "agenda" && (
                item.id === pending.agendaId ||
                (isAgenda(item) && item.agenda_id?.toString() === pending.agendaId)
              )
            );

            if (agendaIndex !== -1 && pending.speakerData) {
              const agenda = updated[agendaIndex] as IAgenda;
              const newSpeaker: Speaker = {
                id: Date.now(),
                createdBy: userRole || USER.USER_ROLE.ADMIN,
                type: "speaker",
                speaker_id: pending.speakerData.speaker_id,
                duration: pending.speakerData.duration,
                timestamp: pending.speakerData.timestamp,
                text: pending.speakerData.text,
                speech_id: pending.backendSpeechId,
                sequence: (pending.backendSequence ?? pending.insertAfterSequence ?? 0) as number,
                status: "transcribed",
                is_manually_added: true,
                synced: true,
              };

              if (!agenda.speakers) {
                agenda.speakers = [];
              }

              const insertIndex = pending.afterIndex + 1;
              agenda.speakers.splice(insertIndex, 0, newSpeaker);
              updated[agendaIndex] = { ...agenda };
              console.log(`✅ Fallback: Manually added speaker ${pending.backendSpeechId} to agenda ${pending.agendaId} at index ${insertIndex}`);

              // Note: Regenerate button is always visible for admin/super admin
              if (pending.agendaId) {
                console.log(`➕ Speaker added to agenda ${pending.agendaId} via fallback`);
              }

              return stableAgendaSort(updated);
            }
            return prev;
          });
        } else {
          // Standalone speaker - add as fallback
          console.log(`🔄 Fallback: Adding standalone speaker ${pending.backendSpeechId} manually after timeout`);
          setTranscriptions((prev) => {
            const updated = [...prev];

            // Check if speaker already exists (might have been added by SSE or optimistic update)
            const speakerExists = updated.some(
              (item) => item.type === "speaker" &&
                isSpeaker(item) &&
                item.speech_id === pending.backendSpeechId
            );

            if (!speakerExists && pending.speakerData) {
              const insertIndex = pending.afterIndex + 1;
              const newSpeaker: Speaker = {
                id: Date.now(),
                createdBy: userRole || USER.USER_ROLE.ADMIN,
                type: "speaker",
                speaker_id: pending.speakerData.speaker_id,
                duration: pending.speakerData.duration,
                timestamp: pending.speakerData.timestamp,
                text: pending.speakerData.text,
                speech_id: pending.backendSpeechId,
                sequence: pending.backendSequence ?? pending.insertAfterSequence ?? 0,
                status: "transcribed",
                is_manually_added: true,
                synced: true, // Mark as synced since we're adding it manually
              };

              updated.splice(insertIndex, 0, newSpeaker);
              console.log(`✅ Fallback: Manually added standalone speaker ${pending.backendSpeechId} at index ${insertIndex}`);
              return stableAgendaSort(updated);
            } else if (speakerExists) {
              console.log(`✅ Fallback: Speaker ${pending.backendSpeechId} already exists, no action needed`);
            }

            return prev;
          });
        }
      }

      // Clean up pending insertion
      if (pending) {
        pendingInsertionsMapRef.current.delete(`pending-${afterSpeaker.speech_id}`);
      }
    }, 10000); // 10 second timeout

    // Store pending insertion info for SSE reconciliation
    pendingInsertionsMapRef.current.set(`pending-${afterSpeaker.speech_id}`, {
      afterSpeechId: afterSpeaker.speech_id,
      afterIndex: addSpeakerAfterIndex,
      tempSpeechId: `pending-${afterSpeaker.speech_id}`, // Not used for temp node, just for tracking
      tempId: 0, // Not used since we're not creating temp node
      timeoutId,
      insertAfterSequence: insert_after_sequence,
      isInAgenda: !!targetAgenda,
      agendaId: agendaId,
      speakerData: {
        speaker_id: data.speaker_id,
        text: data.text,
        duration: data.duration || "00:00",
        timestamp: data.timestamp || new Date().toISOString(),
      },
    });

    setIsAddingSpeaker(true);

    try {
      const url = `${baseURL}speaker/${mergedMeetingId}/add`;

      // Build request payload - include agenda_id and insert_position_in_agenda if speaker is in agenda
      const requestPayload: any = {
        insert_after_sequence: insert_after_sequence,
        speaker_id: data.speaker_id,
        text: data.text,
        duration: data.duration || "00:00",
        timestamp: data.timestamp || new Date().toISOString(),
      };

      // Add agenda-specific parameters if speaker is in an agenda
      if (targetAgenda && agendaId) {
        requestPayload.agenda_id = agendaId;
        if (insertPositionInAgenda !== null) {
          requestPayload.insert_position_in_agenda = insertPositionInAgenda;
        }
        console.log(`📋 Adding speaker inside agenda ${agendaId} at position ${insertPositionInAgenda}`);
      }

      const response = await axios.post<{
        success: boolean;
        sequence?: number;
        message: string;
        speech_id: string;
        location: string;
        agenda_id?: string;
        speaker_index?: number;
        total_speakers?: number;
      }>(
        url,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(COMMON.ACCESS_TOKEN)}`,
          },
        }
      );

      if (response.data.location?.startsWith('agenda:')) {
        console.log(`✅ API Success - Speaker added to agenda at index ${response.data.speaker_index}`);
      } else {
        console.log(`✅ API Success - Speaker section will be inserted at sequence ${response.data.sequence}`);
      }

      // OPTIMISTIC UPDATE: Add speaker immediately (before SSE event)
      // This provides instant feedback to the user, especially important for standalone speakers
      if (response.data?.speech_id) {
        // Find and update pending insertion with backend speech_id for SSE matching
        for (const [, pendingInsertion] of Array.from(pendingInsertionsMapRef.current.entries())) {
          if (pendingInsertion.afterSpeechId === afterSpeaker.speech_id &&
            pendingInsertion.afterIndex === addSpeakerAfterIndex) {
            pendingInsertion.backendSpeechId = response.data.speech_id;
            pendingInsertion.backendSequence = response.data.sequence;
            pendingInsertion.location = response.data.location;
            console.log(`✅ Stored backend speech_id ${response.data.speech_id} for SSE reconciliation (location: ${response.data.location})`);
            break;
          }
        }

        // OPTIMISTIC UPDATE: Add speaker immediately if it's standalone (not in agenda)
        if (!response.data.location?.startsWith('agenda:') && !response.data.agenda_id) {
          setTranscriptions((prev) => {
            const updated = [...prev];

            // Find the insertion point (after the target speaker)
            const insertIndex = actualIndex + 1;

            // Check if speaker already exists (prevent duplicates)
            const speakerExists = updated.some(
              (item) => item.type === "speaker" &&
                isSpeaker(item) &&
                item.speech_id === response.data.speech_id
            );

            if (!speakerExists) {
              const newSpeaker: Speaker = {
                id: Date.now(),
                createdBy: userRole || USER.USER_ROLE.ADMIN,
                type: "speaker",
                speaker_id: data.speaker_id,
                duration: data.duration || "00:00",
                timestamp: data.timestamp || new Date().toISOString(),
                text: data.text,
                speech_id: response.data.speech_id,
                sequence: response.data.sequence || insert_after_sequence || 0,
                status: "transcribed",
                is_manually_added: true,
                synced: false, // Mark as not synced yet - will be updated when SSE arrives
              };

              // Insert at the correct position
              updated.splice(insertIndex, 0, newSpeaker);
              console.log(`✅ Optimistic update: Added standalone speaker ${response.data.speech_id} at index ${insertIndex}`);
              return stableAgendaSort(updated);
            } else {
              console.log(`⚠️ Speaker ${response.data.speech_id} already exists, skipping optimistic update`);
            }

            return prev;
          });
        }

        // If speaker was added to agenda, wait for ADD_SPEAKER_TO_AGENDA event to sync data
        if (response.data.location?.startsWith('agenda:') || response.data.agenda_id) {
          console.log(`📌 Speaker added to agenda - waiting for ADD_SPEAKER_TO_AGENDA event to sync (speech_id: ${response.data.speech_id})`);
        } else {
          console.log(`📌 Waiting for SSE event to sync speaker data (speech_id: ${response.data.speech_id}) - optimistic update already shown`);
        }
      }
    } catch (error: any) {
      console.error("Error adding speaker section:", error);

      // Remove optimistic update on error (if we had added one)
      // Note: We can't easily track if we added one, so we'll let the timeout handle cleanup

      // Cleanup on error
      for (const [pendingKey, pending] of Array.from(pendingInsertionsMapRef.current.entries())) {
        if (pending.afterSpeechId === afterSpeaker.speech_id &&
          pending.afterIndex === addSpeakerAfterIndex) {
          clearTimeout(pending.timeoutId);

          // Remove optimistic update if it exists
          if (pending.backendSpeechId) {
            setTranscriptions((prev) => {
              const updated = [...prev];
              const speakerIndex = updated.findIndex(
                (item) => item.type === "speaker" &&
                  isSpeaker(item) &&
                  item.speech_id === pending.backendSpeechId
              );

              if (speakerIndex !== -1) {
                updated.splice(speakerIndex, 1);
                console.log(`🗑️ Removed optimistic speaker ${pending.backendSpeechId} due to error`);
                return stableAgendaSort(updated);
              }
              return prev;
            });
          }

          pendingInsertionsMapRef.current.delete(pendingKey);
          break;
        }
      }

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to add speaker section";
      throw new Error(errorMessage);
    } finally {
      setIsAddingSpeaker(false);
      endApiCall();
    }
  };

  /**
   * Handler to open add speaker modal
   */
  const handleOpenAddSpeakerModal = (afterIndex: number) => {
    setAddSpeakerAfterIndex(afterIndex);
    setAddSpeakerModalOpen(true);
  };

  /**
   * Handler to close add speaker modal
   */
  const handleCloseAddSpeakerModal = () => {
    setAddSpeakerModalOpen(false);
    setAddSpeakerAfterIndex(null);
    setAddSpeakerAgendaContext(null); // Clear agenda context
    // Don't clear pending insertions - let them complete or timeout
  };

  /**
   * Handler to open add speaker modal for speakers inside agendas
   * Stores agenda context for when the speaker doesn't exist as standalone
   */
  const handleOpenAddSpeakerModalForAgenda = (agendaIndex: number, speakerIndex: number, speaker: Speaker) => {
    const agenda = transcriptions[agendaIndex];
    if (!agenda || !isAgenda(agenda)) {
      console.error("Agenda not found at index", agendaIndex);
      return;
    }

    // CRITICAL: Use top-level UUID id FIRST (this is what backend expects: "9cd36c0c-69c5-4c1e-93f0-6821758dd499")
    // Backend matches by data.agenda_id (UUID at top level), not data.agenda.agenda_id (numeric)
    // Structure in Redis: { data: { agenda_id: "UUID", agenda: { agenda_id: number, ... } } }
    // User sends: agenda_id: "UUID" (top-level), not the nested numeric one
    // CRITICAL: Always prioritize agenda.id (UUID) over agenda.agenda_id (numeric)
    const agendaId = agenda.id || null;
    if (!agendaId && agenda.agenda_id) {
      console.warn("⚠️ Using nested numeric agenda_id as fallback - this may cause issues. Agenda should have top-level UUID id.");
    }
    if (!agendaId) {
      console.error("Agenda has no ID or agenda_id", agenda);
      return;
    }

    console.log(`📋 Opening add speaker modal for agenda:`, {
      agendaId,
      agendaIdType: agenda.id ? 'id (UUID - top level)' : 'agenda_id (nested numeric)',
      agendaIdFromId: agenda.id,
      agendaIdFromAgenda: agenda.agenda_id?.toString(),
      agendaIndex,
      speakerIndex,
      speakerSpeechId: speaker.speech_id
    });

    // Find the speaker in the transcriptions array (might exist as standalone with in_agenda flag)
    const speakerInTranscriptions = transcriptions.findIndex(
      (item) => item.type === "speaker" &&
        isSpeaker(item) &&
        item.speech_id === speaker.speech_id
    );

    if (speakerInTranscriptions !== -1) {
      // Found as standalone, use that index (clear agenda context)
      setAddSpeakerAgendaContext(null);
      handleOpenAddSpeakerModal(speakerInTranscriptions);
    } else {
      // Speaker only exists in agenda - store agenda context and use a dummy index
      // The actual insertion will use agenda context
      setAddSpeakerAgendaContext({
        agendaId,
        agendaIndex,
        speakerIndex,
        speaker
      });
      // Use -1 as a marker that we're in agenda context
      setAddSpeakerAfterIndex(-1);
      setAddSpeakerModalOpen(true);
      console.log(`📋 Opening add speaker modal for agenda ${agendaId}, speaker index ${speakerIndex}`);
    }
  };

  /**
   * Handler to open edit speaker modal
   */
  const handleOpenEditSpeakerModal = (speaker: Speaker) => {
    setEditingSpeaker(speaker);
    console.log("Editing speaker:", speaker);
    setEditSpeakerModalOpen(true);
  };

  /**
   * Handler to close edit speaker modal
   */
  const handleCloseEditSpeakerModal = () => {
    setEditSpeakerModalOpen(false);
    setEditingSpeaker(null);
  };

  /**
   * Handler to edit speaker section
   */
  const handleEditSpeaker = async (text: string) => {
    if (!mergedMeetingId || !editingSpeaker || !editingSpeaker.speech_id) {
      throw new Error("Meeting ID or speaker not found");
    }

    if (!text.trim()) {
      throw new Error("Text cannot be empty");
    }

    setIsEditingSpeaker(true);
    startApiCall();

    try {
      const url = `${baseURL}speaker/${mergedMeetingId}/edit/${editingSpeaker.speech_id}`;
      const response = await axios.put(
        url,
        { text: text.trim() },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(COMMON.ACCESS_TOKEN)}`,
          },
        }
      );

      console.log(`✅ Speaker section updated successfully`);

      // Check if speaker is in an agenda and mark agenda for summary regeneration
      const speechId = editingSpeaker.speech_id;
      if (speechId) {
        // Find which agenda(s) contain this speaker
        transcriptions.forEach((item) => {
          if (item.type === "agenda" && isAgenda(item)) {
            const agenda = item as IAgenda;
            if (agenda.speakers && agenda.speakers.some((s: Speaker) => s.speech_id === speechId)) {
              // Note: Regenerate button is always visible for admin/super admin
              console.log(`📝 Speaker edited in agenda ${agenda.id}`);
            }
          }
        });
      }

      // Check if intervention summary needs regeneration
      const needsRegeneration = response.data?.needs_intervention_regeneration || false;
      const hasExistingSummary = editingSpeaker.summary && editingSpeaker.summary.length > 0;

      // Auto-regenerate intervention summary if it exists and needs regeneration
      if (needsRegeneration && hasExistingSummary && speechId) {
        console.log(`🔄 Auto-regenerating intervention summary for speaker ${speechId}`);

        // Wait for SSE to update the text in state, then regenerate
        // The update_speaker SSE event will update the transcriptions state with the new text
        setTimeout(() => {
          // Use ref to get the latest transcriptions state
          const currentTranscriptions = transcriptionsRef.current;

          // Find the speaker in transcriptions (could be standalone or in agenda)
          let found = false;

          // First, check if it's a standalone speaker
          const standaloneIndex = currentTranscriptions.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (standaloneIndex !== -1) {
            found = true;
            const speaker = currentTranscriptions[standaloneIndex] as Speaker;
            // Only regenerate if summary exists
            if (speaker.summary) {
              console.log(`🔄 Regenerating intervention summary for standalone speaker at index ${standaloneIndex}`);
              generateSummary(standaloneIndex).catch((error) => {
                console.error("Error regenerating intervention summary:", error);
              });
            }
          } else {
            // Check if speaker is in an agenda
            for (let agendaIndex = 0; agendaIndex < currentTranscriptions.length; agendaIndex++) {
              const item = currentTranscriptions[agendaIndex];
              if (item.type === "agenda" && isAgenda(item)) {
                const agenda = item as IAgenda;
                if (agenda.speakers) {
                  const speakerIndex = agenda.speakers.findIndex(
                    (s: Speaker) => s.speech_id === speechId
                  );

                  if (speakerIndex !== -1) {
                    found = true;
                    const speaker = agenda.speakers[speakerIndex];
                    // Only regenerate if summary exists
                    if (speaker.summary) {
                      console.log(`🔄 Regenerating intervention summary for speaker in agenda ${agenda.id} at speaker index ${speakerIndex}`);
                      generateSummary(speakerIndex, agendaIndex).catch((error) => {
                        console.error("Error regenerating intervention summary:", error);
                      });
                    }
                    break;
                  }
                }
              }
            }
          }

          if (!found) {
            console.warn(`⚠️ Speaker ${speechId} not found in transcriptions for summary regeneration`);
          }
        }, 1000); // Delay to allow SSE update_speaker event to process and update state
      }

      handleCloseEditSpeakerModal();
    } catch (error: any) {
      console.error("Error editing speaker section:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to edit speaker section";
      throw new Error(errorMessage);
    } finally {
      setIsEditingSpeaker(false);
      endApiCall();
    }
  };

  /**
   * Handler to open delete confirmation
   */
  const handleOpenDeleteConfirm = (speaker: Speaker) => {
    setDeletingSpeaker(speaker);
    setDeleteConfirmOpen(true);
  };

  /**
   * Handler to close delete confirmation
   */
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeletingSpeaker(null);
  };

  /**
   * Handler to delete speaker section
   */
  const handleDeleteSpeaker = async () => {
    if (!mergedMeetingId || !deletingSpeaker || !deletingSpeaker.speech_id) {
      throw new Error("Meeting ID or speaker not found");
    }

    const speechIdToDelete = deletingSpeaker.speech_id;
    setIsDeletingSpeaker(true);
    startApiCall();

    // Track deleted speaker to prevent re-adding from delayed SSE messages
    deletedSpeakersRef.current.add(speechIdToDelete);

    // Check if speaker is in an agenda and mark agenda for summary regeneration
    transcriptions.forEach((item) => {
      if (item.type === "agenda" && isAgenda(item)) {
        const agenda = item as IAgenda;
        if (agenda.speakers && agenda.speakers.some((s: Speaker) => s.speech_id === speechIdToDelete)) {
          // Note: Regenerate button is always visible for admin/super admin
          console.log(`🗑️ Speaker deleted from agenda ${agenda.id}`);
        }
      }
    });

    // Optimistic update: Remove speaker immediately from UI
    setTranscriptions((prev) => {
      const updated = [...prev];
      let speakerRemoved = false;

      // Remove standalone speaker
      const standaloneIndex = updated.findIndex(
        (item) => item.type === "speaker" && item.speech_id === speechIdToDelete
      );

      if (standaloneIndex !== -1) {
        updated.splice(standaloneIndex, 1);
        speakerRemoved = true;
        console.log(`🗑️ Optimistically removed standalone speaker ${speechIdToDelete}`);
      }

      // Also remove from any agendas that contain this speaker
      updated.forEach((item, index) => {
        if (item.type === "agenda" && isAgenda(item)) {
          const agenda = item as IAgenda;
          if (agenda.speakers && agenda.speakers.some(s => s.speech_id === speechIdToDelete)) {
            const updatedSpeakers = agenda.speakers.filter(s => s.speech_id !== speechIdToDelete);
            updated[index] = {
              ...agenda,
              speakers: updatedSpeakers,
            } as IAgenda;
            speakerRemoved = true;
            console.log(`🗑️ Optimistically removed speaker ${speechIdToDelete} from agenda ${agenda.id}`);
          }
        }
      });

      if (speakerRemoved) {
        return stableAgendaSort(updated);
      }

      return prev;
    });

    try {
      const url = `${baseURL}speaker/${mergedMeetingId}/delete/${speechIdToDelete}`;
      await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(COMMON.ACCESS_TOKEN)}`,
        },
      });

      console.log(`✅ Speaker section deleted successfully`);
      handleCloseDeleteConfirm();
      // Note: SSE event will also update the UI, but optimistic update already removed it
    } catch (error: any) {
      console.error("Error deleting speaker section:", error);

      // Revert optimistic update on error by refreshing from server
      // The SSE stream will maintain correct state, but we could also fetch here if needed

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to delete speaker section";
      throw new Error(errorMessage);
    } finally {
      setIsDeletingSpeaker(false);
      endApiCall();
    }
  };

  // API helper function to assign intervention label to speaker
  const assignInterventionLabelToSpeaker = async (speechId: string, labelId: number) => {
    startApiCall();
    try {
      const url = `${baseURL}realtime-reporting/assign-intervention-label`;
      await axios.post(url, {
        speech_id: speechId,
        intervention_label_id: labelId
      });
    } catch (error) {
      console.error("Error assigning intervention label:", error);
    } finally {
      endApiCall();
    }
  };

  // API helper function to remove intervention label from speaker
  const removeInterventionLabelFromSpeaker = async (speechId: string) => {
    startApiCall();
    try {
      const url = `${baseURL}realtime-reporting/remove-intervention-label`;
      await axios.post(url, {
        speech_id: speechId
      });
    } catch (error) {
      console.error("Error removing intervention label:", error);
    } finally {
      endApiCall();
    }
  };

  /**
   * Handler to open delete intervention summary confirmation
   */
  const handleOpenDeleteInterventionSummaryConfirm = (
    speechId: string,
    index: number,
    agendaIndex?: number | null
  ) => {
    setDeletingInterventionSummary({ speechId, index, agendaIndex });
    setDeleteInterventionSummaryConfirmOpen(true);
  };

  /**
   * Handler to close delete intervention summary confirmation
   */
  const handleCloseDeleteInterventionSummaryConfirm = () => {
    setDeleteInterventionSummaryConfirmOpen(false);
    setDeletingInterventionSummary(null);
  };

  /**
   * Handler to delete intervention summary
   */
  const handleDeleteInterventionSummary = async () => {
    if (!uniqueMeetingId || !deletingInterventionSummary || !meetingDbId) {
      return;
    }

    setIsDeletingInterventionSummary(true);
    startApiCall();

    try {
      const url = `${baseURL}${RestfulUrls.DELETE_INTERVENTION_SUMMARY}`;
      const payload = {
        speech_id: deletingInterventionSummary.speechId,
        meeting_id: uniqueMeetingId, // Use just the meeting ID, not the merged one
        meetingDbId: meetingDbId,
      };

      console.log(`🗑️ Deleting intervention summary with payload:`, payload);
      console.log(`📤 DELETE request to: ${url}`);

      await axios.delete(url, {
        data: payload,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(COMMON.ACCESS_TOKEN)}`,
          "Content-Type": "application/json",
        },
      });

      console.log(`✅ Intervention summary deleted successfully for speech_id: ${deletingInterventionSummary.speechId}`);

      // Optimistically update the UI - clear the summary
      setTranscriptions((prev) => {
        const updated = [...prev];
        const { index, agendaIndex } = deletingInterventionSummary;

        if (agendaIndex !== null && agendaIndex !== undefined) {
          // Speaker is inside an agenda
          const agenda = updated[agendaIndex];
          if (isAgenda(agenda) && agenda.speakers) {
            const speakerIndex = agenda.speakers.findIndex(
              (s: Speaker) => s.speech_id === deletingInterventionSummary.speechId
            );
            if (speakerIndex !== -1) {
              const updatedAgenda = { ...agenda };
              const updatedSpeakers = [...updatedAgenda.speakers];
              updatedSpeakers[speakerIndex] = {
                ...updatedSpeakers[speakerIndex],
                summary: "",
                country: "",
                intervention_label_id: undefined,
                intervention_label_name: "",
                summaryTimestamp: undefined,
                createdBy: "",
              };
              updatedAgenda.speakers = updatedSpeakers;
              updated[agendaIndex] = updatedAgenda;
            }
          }
        } else {
          // Standalone speaker
          if (index < updated.length && isSpeaker(updated[index])) {
            const speaker = updated[index] as Speaker;
            if (speaker.speech_id === deletingInterventionSummary.speechId) {
              updated[index] = {
                ...speaker,
                summary: "",
                country: "",
                intervention_label_id: undefined,
                intervention_label_name: "",
                summaryTimestamp: undefined,
                createdBy: "",
              };
            }
          }
        }

        return stableAgendaSort(updated);
      });

      // Show success message
      dispatch({
        type: COMMON.SHOW_SNACKBAR,
        payload: {
          message: "Intervention summary deleted successfully",
          severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
        },
      });

      handleCloseDeleteInterventionSummaryConfirm();
    } catch (error: any) {
      console.error("Error deleting intervention summary:", error);

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to delete intervention summary";

      // Show error message
      dispatch({
        type: COMMON.SHOW_SNACKBAR,
        payload: {
          message: errorMessage,
          severity: COMMON.SNACKBAR_SEVERITY.ERROR,
        },
      });
    } finally {
      setIsDeletingInterventionSummary(false);
      endApiCall();
    }
  };

  useEffect(() => {
    if (editSummModalOpen) {
      fetchCountries();
    }
  }, [editSummModalOpen]); // Empty dependency array means this runs only once when the component mounts

  const startStream = (meetingId: string | undefined, shouldRecord: boolean = true) => {
    if (startRecordingEventSource?.current != null && startRecordingEventSource && startRecordingEventSource?.current?.readyState !== EventSource.CLOSED) {
      return;
    }
    const url = `${baseURL}realtime-streaming/stream/${meetingId}/${meetingDbId}`;
    const key = `stream-${meetingId}_${meetingDbId}`;
    joinMeetingAgendaStreamStartedRef.current = false;

    if (activeEventSources.has(key)) {
      return;
    }

    setTranscriptions([]);
    setLoading(true);
    dispatch({ type: "START_RECORDING", payload: shouldRecord });
    setShowLoader(true);

    startRecordingEventSource.current = new EventSource(url);
    setLoading(false);
    startRecordingEventSource.current.onmessage = (event: MessageEvent) => {
      const payload = JSON.parse(event.data);

      // Debug logging
      console.log("SSE EVENT RECEIVED:", payload);

      // Handle update_speaker type messages
      if (payload.type === "update_speaker" || payload.data?.type === "update_speaker") {
        const updatedSpeakerData = payload.data || payload;
        const speechId = updatedSpeakerData.speech_id;

        if (!speechId) {
          console.warn("update_speaker message missing speech_id, ignoring", payload);
          return;
        }

        // Use new_text from the message, fallback to text if new_text not available
        const newText = updatedSpeakerData.new_text ?? updatedSpeakerData.text;

        if (!newText) {
          console.warn("update_speaker message missing new_text/text, ignoring", payload);
          return;
        }

        console.log(`🔄 Processing update_speaker event for speech_id: ${speechId}`, {
          old_text: updatedSpeakerData.old_text,
          new_text: newText,
          updated_at: updatedSpeakerData.updated_at
        });

        setTranscriptions((prev) => {
          const updated = [...prev];
          let speakerUpdated = false;

          // Update standalone speaker
          const standaloneIndex = updated.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (standaloneIndex !== -1) {
            const existingSpeaker = updated[standaloneIndex] as Speaker;
            updated[standaloneIndex] = {
              ...existingSpeaker,
              text: newText, // Use new_text from the update message
              // Update sequence if provided in the message
              sequence: updatedSpeakerData.sequence ?? existingSpeaker.sequence,
              // Preserve other fields like summary, isExpanded, country, etc.
            };
            speakerUpdated = true;
            console.log(`✅ Updated standalone speaker at index ${standaloneIndex}`, {
              old_text: updatedSpeakerData.old_text,
              new_text: newText
            });
          }

          // Update speaker within agendas
          updated.forEach((item, index) => {
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              const speakerIndex = agenda.speakers?.findIndex(
                (s: Speaker) => s.speech_id === speechId
              );

              if (speakerIndex !== -1 && agenda.speakers) {
                const existingAgendaSpeaker = agenda.speakers[speakerIndex];
                const updatedSpeakers = [...agenda.speakers];
                updatedSpeakers[speakerIndex] = {
                  ...existingAgendaSpeaker,
                  text: newText, // Use new_text from the update message
                  // Update sequence if provided in the message
                  sequence: updatedSpeakerData.sequence ?? existingAgendaSpeaker.sequence,
                  // Preserve other fields like summary, isExpanded, country, etc.
                };

                updated[index] = {
                  ...agenda,
                  speakers: updatedSpeakers,
                };
                speakerUpdated = true;
                console.log(`✅ Updated speaker in agenda ${agenda.id} at speaker index ${speakerIndex}`, {
                  old_text: updatedSpeakerData.old_text,
                  new_text: newText
                });
              }
            }
          });

          if (speakerUpdated) {
            return stableAgendaSort(updated);
          }

          // If speaker not found, log warning but don't update state
          console.warn(`⚠️ update_speaker: Speaker with speech_id ${speechId} not found in transcriptions`);
          return prev;
        });

        return; // Exit early for update_speaker messages
      }

      // Handle AGENDA_SPEAKER_UPDATED type messages
      if (payload.type === "AGENDA_SPEAKER_UPDATED" || payload.data?.type === "AGENDA_SPEAKER_UPDATED") {
        const updatedSpeakerData = payload.data || payload;
        const speechId = updatedSpeakerData.speech_id;
        const agendaId = updatedSpeakerData.agenda_id || updatedSpeakerData.agenda?.id;

        if (!speechId) {
          console.warn("AGENDA_SPEAKER_UPDATED message missing speech_id, ignoring", payload);
          return;
        }

        // Use new_text from the message, fallback to text if new_text not available
        const newText = updatedSpeakerData.new_text ?? updatedSpeakerData.text;

        if (!newText) {
          console.warn("AGENDA_SPEAKER_UPDATED message missing new_text/text, ignoring", payload);
          return;
        }

        console.log(`🔄 Processing AGENDA_SPEAKER_UPDATED event for speech_id: ${speechId}`, {
          agenda_id: agendaId,
          old_text: updatedSpeakerData.old_text,
          new_text: newText,
          updated_at: updatedSpeakerData.updated_at
        });

        // Note: Regenerate button is always visible for admin/super admin
        if (agendaId) {
          console.log(`📝 Speaker updated in agenda ${agendaId} via SSE`);
        } else {
          // If no agenda_id provided, find agenda containing this speaker
          transcriptions.forEach((item) => {
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              if (agenda.speakers && agenda.speakers.some((s: Speaker) => s.speech_id === speechId)) {
                console.log(`📝 Speaker updated in agenda ${agenda.id} via SSE`);
              }
            }
          });
        }

        setTranscriptions((prev) => {
          const updated = [...prev];
          let speakerUpdated = false;

          // Update speaker within agendas
          updated.forEach((item, index) => {
            if (item.type === "agenda" && isAgenda(item)) {
              // If agenda_id is provided, only update in that specific agenda
              // Otherwise, update in any agenda that contains the speaker
              if (agendaId && item.id !== agendaId && item.agenda_id !== agendaId) {
                return; // Skip agendas that don't match the agenda_id
              }

              const agenda = item as IAgenda;
              const speakerIndex = agenda.speakers?.findIndex(
                (s: Speaker) => s.speech_id === speechId
              );

              if (speakerIndex !== -1 && agenda.speakers) {
                const existingAgendaSpeaker = agenda.speakers[speakerIndex];
                const updatedSpeakers = [...agenda.speakers];
                updatedSpeakers[speakerIndex] = {
                  ...existingAgendaSpeaker,
                  text: newText, // Use new_text from the update message
                  // Update sequence if provided in the message
                  sequence: updatedSpeakerData.sequence ?? existingAgendaSpeaker.sequence,
                  // Preserve other fields like summary, isExpanded, country, etc.
                };

                updated[index] = {
                  ...agenda,
                  speakers: updatedSpeakers,
                };
                speakerUpdated = true;
                console.log(`✅ Updated speaker in agenda ${agenda.id} (agenda_id: ${agenda.agenda_id}) at speaker index ${speakerIndex}`, {
                  old_text: updatedSpeakerData.old_text,
                  new_text: newText
                });
              }
            }
          });

          if (speakerUpdated) {
            return stableAgendaSort(updated);
          }

          // If speaker not found, log warning but don't update state
          console.warn(`⚠️ AGENDA_SPEAKER_UPDATED: Speaker with speech_id ${speechId} not found in any agenda`, {
            agenda_id: agendaId
          });
          return prev;
        });

        return; // Exit early for AGENDA_SPEAKER_UPDATED messages
      }

      // Handle DELETE_SPEAKER type messages (from main channel)
      if (payload.type === "DELETE_SPEAKER" || payload.data?.type === "DELETE_SPEAKER") {
        const deleteData = payload.data || payload;
        const speechId = deleteData.speech_id;

        if (!speechId) {
          console.warn("DELETE_SPEAKER message missing speech_id, ignoring", payload);
          return;
        }

        console.log(`🗑️ Processing DELETE_SPEAKER event (from main stream) for speech_id: ${speechId}`);

        // Track deleted speaker to prevent re-adding from delayed SSE messages
        deletedSpeakersRef.current.add(speechId);

        setTranscriptions((prev) => {
          const updated = [...prev];
          let speakerRemoved = false;

          // Remove standalone speaker
          const standaloneIndex = updated.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (standaloneIndex !== -1) {
            updated.splice(standaloneIndex, 1);
            speakerRemoved = true;
            console.log(`✅ Removed standalone speaker with speech_id ${speechId} (from main stream)`);
          }

          // Also remove from any agendas that contain this speaker
          updated.forEach((item, index) => {
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              if (agenda.speakers && agenda.speakers.some(s => s.speech_id === speechId)) {
                const updatedSpeakers = agenda.speakers.filter(s => s.speech_id !== speechId);
                updated[index] = {
                  ...agenda,
                  speakers: updatedSpeakers,
                } as IAgenda;
                speakerRemoved = true;

                // Note: Regenerate button is always visible for admin/super admin
                if (agenda.id) {
                  console.log(`🗑️ Speaker deleted from agenda ${agenda.id} via main stream`);
                }

                console.log(`✅ Removed speaker ${speechId} from agenda ${agenda.id} (from main stream)`);
              }
            }
          });

          if (speakerRemoved) {
            console.log(`✅ Removed speaker with speech_id ${speechId} from transcriptions (from main stream)`);
            return stableAgendaSort(updated);
          } else {
            console.warn(`⚠️ DELETE_SPEAKER: Speaker with speech_id ${speechId} not found in transcriptions (from main stream)`);
            return prev;
          }
        });

        return; // Exit early for DELETE_SPEAKER messages
      }

      // Parse sequence from top level (required format: {sequence, data, prev_speech_id?})
      const sequence = payload.sequence;
      const data = payload.data || {}; // Use data field from wrapped format

      // Ignore messages without sequence (critical for ordering)
      if (sequence == null) {
        console.warn("SSE missing sequence, ignoring", payload);
        return;
      }

      // Render both completed (transcribed) AND interim (transcribing) messages for live word-by-word display
      // Manually added speakers are always "transcribed" so they should always be processed
      const isManuallyAdded = data.is_manually_added === true;
      const isTranscribed = data.status === "transcribed";
      const isTranscribing = data.status === "transcribing";

      // Log manually added speakers for debugging
      if (isManuallyAdded) {
        console.log(`🔵 Manually added speaker detected: sequence=${sequence}, speech_id=${data.speech_id}, prev_sequence_id=${payload.prev_sequence_id ?? data.prev_sequence_id}, prev_speech_id=${payload.prev_speech_id ?? data.prev_speech_id}, status=${data.status}`);
      }

      // Handle channel events (keep-alive, channel_created, etc.) but don't skip transcribing messages
      if (!isTranscribed && !isManuallyAdded && !isTranscribing) {
        // Check for channel events
        if (
          !joinMeetingAgendaStreamStartedRef.current &&
          (data && (data['channel_created'] || data['channel_started']))
        ) {
          joinMeetingAgendaStreamStartedRef.current = true;
          setTimeout(() => {
            startAgendaStream(meetingId);
          }, 5000);
        }
        return;
      }

      // Start agenda stream on first transcribing/transcribed event
      if (
        !joinMeetingAgendaStreamStartedRef.current &&
        (isTranscribing || isTranscribed)
      ) {
        joinMeetingAgendaStreamStartedRef.current = true;
        setTimeout(() => {
          startAgendaStream(meetingId);
        }, 5000);
      }

      // Handle interim "transcribing" messages for live word-by-word display
      if (isTranscribing) {
        console.log(`📝 Live transcribing update: speech_id=${data.speech_id}, text="${data.text?.substring(0, 50)}..."`);

        setTranscriptions((prev) => {
          const speechId = data.speech_id;

          // Skip if no speech_id or no text
          if (!speechId || !data.text) {
            return prev;
          }

          // Check if speaker was deleted
          if (deletedSpeakersRef.current.has(speechId)) {
            return prev;
          }

          let updated = [...prev];

          // Find existing speaker with this speech_id (both standalone and in agendas)
          const existingStandaloneIndex = updated.findIndex(
            (item) => item.type === "speaker" && item.speech_id === speechId
          );

          if (existingStandaloneIndex !== -1) {
            // Update existing standalone speaker with interim text
            const existingSpeaker = updated[existingStandaloneIndex] as Speaker;
            updated[existingStandaloneIndex] = {
              ...existingSpeaker,
              text: data.text,
              status: "transcribing",
            };
            return updated;
          }

          // Check if speaker exists in any agenda
          for (let i = 0; i < updated.length; i++) {
            const item = updated[i];
            if (item.type === "agenda" && isAgenda(item)) {
              const agenda = item as IAgenda;
              const speakerIndex = agenda.speakers?.findIndex(
                (s: Speaker) => s.speech_id === speechId
              );
              if (speakerIndex !== undefined && speakerIndex !== -1 && agenda.speakers) {
                // Update speaker within agenda with interim text
                const updatedSpeakers = [...agenda.speakers];
                updatedSpeakers[speakerIndex] = {
                  ...updatedSpeakers[speakerIndex],
                  text: data.text,
                  status: "transcribing",
                };
                updated[i] = {
                  ...agenda,
                  speakers: updatedSpeakers,
                };
                return updated;
              }
            }
          }

          // For transcribing (interim) - just create/update speaker with this speech_id
          // Merging only happens for transcribed messages
          const newSpeaker: Speaker = {
            id: Date.now(),
            createdBy: userRole ? userRole : USER.USER_ROLE.ADMIN,
            type: "speaker",
            speaker_id: data.speaker_id || "Unknown",
            duration: data.duration || "0:00",
            timestamp: payload.timestamp || data.timestamp || new Date().toISOString(),
            text: data.text,
            speech_id: speechId,
            summary: null,
            country: data.country,
            sequence: Number(sequence),
            prev_sequence_id: payload.prev_sequence_id ?? data.prev_sequence_id ?? undefined,
            prev_speech_id: payload.prev_speech_id ?? data.prev_speech_id ?? undefined,
            status: "transcribing",
            is_manually_added: false,
            synced: false, // Interim messages are not yet synced
          };

          // Add the new speaker for live display
          updated.push(newSpeaker);
          return stableAgendaSort(updated);
        });

        return; // Don't continue to regular processing for transcribing messages
      }

      // For manually added speakers, ensure they have transcribed status
      if (isManuallyAdded && !isTranscribed) {
        console.log(`⚠️ Manually added speaker has non-transcribed status, fixing: ${data.status} -> transcribed`);
        data.status = "transcribed";
      }

      setTranscriptions((prev) => {
        // Prevent duplicates from SSE and reconciliation flow
        const incomingSpeaker: Speaker = {
          id: payload.data?.id ?? Date.now(),
          createdBy: userRole ? userRole : USER.USER_ROLE.ADMIN,
          type: "speaker",
          speaker_id: payload.data?.speaker_id || "Unknown",
          duration: payload.data?.duration || "0:00",
          timestamp: payload.timestamp || payload.data?.timestamp,
          text: payload.data?.text || "",
          speech_id: payload.data?.speech_id,
          summary: payload.data?.summary,
          country: payload.data?.country,
          sequence: Number(payload.sequence),
          prev_sequence_id: payload.prev_sequence_id ?? payload.data?.prev_sequence_id ?? undefined,
          prev_speech_id: payload.prev_speech_id ?? payload.data?.prev_speech_id ?? undefined,
          status: payload.data?.status,
          is_manually_added: payload.data?.is_manually_added ?? false,
          synced: true, // SSE messages are authoritative
        };

        // Debug logging for manually added speakers
        if (incomingSpeaker.is_manually_added) {
          console.log(`🔵 Processing manually added speaker:`, {
            sequence: incomingSpeaker.sequence,
            speech_id: incomingSpeaker.speech_id,
            prev_sequence_id: incomingSpeaker.prev_sequence_id,
            prev_speech_id: incomingSpeaker.prev_speech_id,
            text: incomingSpeaker.text,
            currentTranscriptionsLength: prev.length
          });
        }

        // CRITICAL: Check if this speaker was deleted - prevent re-adding from delayed SSE messages
        if (incomingSpeaker.speech_id && deletedSpeakersRef.current.has(incomingSpeaker.speech_id)) {
          console.log(`🛡️ Blocked re-adding deleted speaker ${incomingSpeaker.speech_id} from SSE stream`);
          return prev; // Don't add deleted speakers
        }

        // CRITICAL FIX: Check if this speaker has in_agenda flag - if so, skip standalone addition
        // Backend sends in_agenda: True when speaker is added to an agenda
        // The ADD_SPEAKER_TO_AGENDA event will handle adding it to the agenda
        const isInAgenda = payload.data?.in_agenda === true || (incomingSpeaker as any).in_agenda === true;
        const agendaIdFromData = payload.data?.agenda_id || (incomingSpeaker as any).agenda_id;

        if (isInAgenda || agendaIdFromData) {
          console.log(`🛡️ Main stream: Skipping standalone addition of speaker ${incomingSpeaker.speech_id} - it has in_agenda flag (agenda_id: ${agendaIdFromData})`);
          console.log(`📋 Waiting for ADD_SPEAKER_TO_AGENDA event from agenda stream to add it to the agenda`);
          return prev; // Don't add as standalone, wait for agenda stream event
        }

        // 2. Check for pending insertions (for manually added speakers - no optimistic update)
        // Match by backend speech_id that was stored after API response
        let matchedPendingInsertion: PendingInsertion | null = null;
        for (const [pendingKey, pendingInsertion] of Array.from(pendingInsertionsMapRef.current.entries())) {
          // Match if SSE speech_id matches the backend speech_id we stored from API response
          if (pendingInsertion.backendSpeechId && pendingInsertion.backendSpeechId === incomingSpeaker.speech_id) {
            console.log("SSE matched pending insertion:", {
              backendSpeechId: pendingInsertion.backendSpeechId,
              incomingSpeechId: incomingSpeaker.speech_id,
              isInAgenda: pendingInsertion.isInAgenda,
              agendaId: pendingInsertion.agendaId
            });
            matchedPendingInsertion = pendingInsertion;
            // Clean up pending insertion
            clearTimeout(pendingInsertion.timeoutId);
            pendingInsertionsMapRef.current.delete(pendingKey);
            break;
          }
        }

        // CRITICAL: If this speaker was added inside an agenda, skip adding it as standalone
        // The ADD_SPEAKER_TO_AGENDA event from the agenda stream will handle adding it to the agenda
        if (matchedPendingInsertion && matchedPendingInsertion.isInAgenda) {
          console.log(`🛡️ Skipping standalone addition of speaker ${incomingSpeaker.speech_id} - it was added inside agenda ${matchedPendingInsertion.agendaId}`);
          console.log(`📋 Waiting for ADD_SPEAKER_TO_AGENDA event to add it to the agenda`);
          return prev; // Don't add as standalone, wait for agenda stream event
        }

        let updated = [...prev];

        // CRITICAL: Check if speaker is already in an agenda before adding as standalone
        // If a speaker is in an agenda's speakers array, it should NOT be in the standalone transcriptions
        // This check must happen BEFORE any modifications to prevent duplicates
        let speakerInAgenda = false;
        let agendaContainingSpeaker: IAgenda | null = null;
        for (const item of updated) {
          if (item.type === "agenda" && isAgenda(item)) {
            const agenda = item as IAgenda;
            if (agenda.speakers && agenda.speakers.some((s: Speaker) =>
              s.speech_id === incomingSpeaker.speech_id || s.id === incomingSpeaker.id
            )) {
              speakerInAgenda = true;
              agendaContainingSpeaker = agenda;
              console.log(`🛡️ Speaker ${incomingSpeaker.speech_id} is already in agenda ${agenda.id}, skipping standalone addition`);
              break;
            }
          }
        }

        // If speaker is in an agenda, update the agenda's speaker data instead of adding standalone
        if (speakerInAgenda && agendaContainingSpeaker) {
          const agendaIndex = updated.findIndex(item => item.id === agendaContainingSpeaker!.id);
          if (agendaIndex !== -1) {
            const agenda = updated[agendaIndex] as IAgenda;
            if (agenda.speakers) {
              const speakerIndex = agenda.speakers.findIndex(
                (s: Speaker) => s.speech_id === incomingSpeaker.speech_id || s.id === incomingSpeaker.id
              );
              if (speakerIndex !== -1) {
                // Update the speaker in the agenda
                agenda.speakers[speakerIndex] = {
                  ...agenda.speakers[speakerIndex],
                  ...incomingSpeaker,
                };
                updated[agendaIndex] = { ...agenda };
                console.log(`✅ Updated speaker ${incomingSpeaker.speech_id} in agenda ${agenda.id}`);
                return stableAgendaSort(updated);
              }
            }
          }
          // If we can't update in agenda, just skip adding as standalone
          return prev;
        }

        // Always replace by speech_id if exists (for standalone speakers)
        const idx = updated.findIndex(
          (item) => item.type === "speaker" && item.speech_id === incomingSpeaker.speech_id
        );

        if (idx !== -1) {
          // Replace existing standalone speaker
          if (incomingSpeaker.is_manually_added) {
            console.log(`🔄 Replacing existing speaker at index ${idx} with manually added speaker ${incomingSpeaker.speech_id}`);
          }
          // Only update if speaker is not in an agenda
          if (!speakerInAgenda) {
            updated[idx] = incomingSpeaker;
          } else {
            // Speaker is in agenda, remove from standalone list
            updated.splice(idx, 1);
            console.log(`🗑️ Removed speaker ${incomingSpeaker.speech_id} from standalone (it's in an agenda)`);
          }
        } else {
          // New speaker - only add if not in an agenda
          if (speakerInAgenda) {
            console.log(`🛡️ Skipping addition of speaker ${incomingSpeaker.speech_id} - it's already in an agenda`);
            return prev; // Don't add speaker that's in an agenda
          }

          // Merging of consecutive same speaker_id sections happens in the merge pass at the end

          if (incomingSpeaker.is_manually_added) {
            console.log(`➕ New manually added speaker, inserting...`, {
              sequence: incomingSpeaker.sequence,
              prev_sequence_id: incomingSpeaker.prev_sequence_id,
              prev_speech_id: incomingSpeaker.prev_speech_id,
              currentSpeakers: updated.filter(item => item.type === "speaker").map(s => ({
                speech_id: (s as Speaker).speech_id,
                sequence: (s as Speaker).sequence
              }))
            });
          }

          // New speaker - insert at correct position
          // Priority: 1. prev_sequence_id (new), 2. prev_speech_id (fallback for old meetings), 3. sequence-based
          let inserted = false;

          // PRIMARY: Try prev_sequence_id first (new method - more reliable)
          if (incomingSpeaker.prev_sequence_id !== undefined && incomingSpeaker.prev_sequence_id !== null) {
            // Find the speaker with matching sequence (prev_sequence_id points to the sequence we should insert after)
            const prevSpeakerIndex = updated.findIndex(
              (item) => item.type === "speaker" && (item as Speaker).sequence === incomingSpeaker.prev_sequence_id
            );

            if (prevSpeakerIndex !== -1) {
              // Insert right after the previous speaker
              updated.splice(prevSpeakerIndex + 1, 0, incomingSpeaker);
              console.log(`✅ Inserted speaker after prev_sequence_id ${incomingSpeaker.prev_sequence_id} at index ${prevSpeakerIndex + 1}`);
              inserted = true;
            } else {
              console.log(`⚠️ prev_sequence_id ${incomingSpeaker.prev_sequence_id} not found, trying fallback methods`);
            }
          }

          // FALLBACK: Try prev_speech_id (for old meetings that don't have prev_sequence_id)
          if (!inserted && incomingSpeaker.prev_speech_id) {
            const prevSpeakerIndex = updated.findIndex(
              (item) => item.type === "speaker" && (item as Speaker).speech_id === incomingSpeaker.prev_speech_id
            );

            if (prevSpeakerIndex !== -1) {
              // Insert right after the previous speaker
              updated.splice(prevSpeakerIndex + 1, 0, incomingSpeaker);
              console.log(`✅ Inserted speaker after prev_speech_id ${incomingSpeaker.prev_speech_id} at index ${prevSpeakerIndex + 1}`);
              inserted = true;
            } else {
              console.log(`⚠️ prev_speech_id ${incomingSpeaker.prev_speech_id} not found, using sequence-based insertion`);
            }
          }

          // FINAL FALLBACK: Use sequence-based insertion
          if (!inserted) {
            const insertIndex = findInsertIndexBySequence(updated, incomingSpeaker.sequence ?? 0);
            if (insertIndex !== -1) {
              updated.splice(insertIndex, 0, incomingSpeaker);
              console.log(`✅ Inserted speaker at sequence ${incomingSpeaker.sequence} at index ${insertIndex}`);
            } else {
              // If still can't find position, append but log warning
              console.warn(`⚠️ Could not find insertion position for speaker ${incomingSpeaker.speech_id} with sequence ${incomingSpeaker.sequence}, appending to end`);
              updated.push(incomingSpeaker);
            }
          }
        }

        // Before sorting, log manually added speakers
        if (incomingSpeaker.is_manually_added) {
          const beforeSort = updated.filter(item => item.type === "speaker" && (item as Speaker).is_manually_added);
          console.log(`📊 Before stableAgendaSort: ${beforeSort.length} manually added speakers in array`);
        }

        updated = stableAgendaSort(updated);

        // After sorting, verify manually added speakers are still there
        if (incomingSpeaker.is_manually_added) {
          const afterSort = updated.filter(item => item.type === "speaker" && (item as Speaker).is_manually_added);
          const found = updated.find(item => item.type === "speaker" && (item as Speaker).speech_id === incomingSpeaker.speech_id);
          console.log(`📊 After stableAgendaSort: ${afterSort.length} manually added speakers in array, current speaker ${found ? 'FOUND' : 'MISSING'}`);
          if (!found) {
            console.error(`❌ CRITICAL: Manually added speaker ${incomingSpeaker.speech_id} (sequence ${incomingSpeaker.sequence}) was LOST after stableAgendaSort!`);
          }
        }

        // CRITICAL FIX: Merge pass - combine consecutive speakers with same speaker_id
        // Only merge non-manually added speakers
        const mergedTranscriptions: Transcription[] = [];
        for (let i = 0; i < updated.length; i++) {
          const currentItem = updated[i];

          // Skip empty text speakers
          if (isSpeaker(currentItem) && currentItem.text.trim() === "") {
            continue;
          }

          if (mergedTranscriptions.length === 0) {
            mergedTranscriptions.push(currentItem);
          } else {
            const lastMerged = mergedTranscriptions[mergedTranscriptions.length - 1];

            // Only merge if both are speakers, same speaker_id, and neither is manually added
            if (
              isSpeaker(lastMerged) &&
              isSpeaker(currentItem) &&
              lastMerged.speaker_id === currentItem.speaker_id &&
              !lastMerged.is_manually_added &&
              !currentItem.is_manually_added
            ) {
              // Merge: append text if not already included
              const currentText = currentItem.text.trim();
              if (currentText && !lastMerged.text.includes(currentText)) {
                lastMerged.text = lastMerged.text + " " + currentText;
              }
              //  Update to LAST speaker's speech_id, sequence, AND timestamp
              // This ensures "Add speaker section" uses the correct position after the merged group
              // and the 60-second delay is calculated from the LAST speaker's timestamp
              lastMerged.speech_id = currentItem.speech_id;
              lastMerged.sequence = currentItem.sequence; // Use last speaker's sequence for correct insertion
              lastMerged.status = currentItem.status;
              lastMerged.timestamp = currentItem.timestamp; //  Use LAST speaker's timestamp for 60s delay calculation
              console.log(` Merged speaker sections (speaker_id: ${currentItem.speaker_id}, updated sequence to ${currentItem.sequence}, timestamp to ${currentItem.timestamp})`);
            } else {
              // Different speaker or agenda - add as new item
              mergedTranscriptions.push(currentItem);
            }
          }
        }

        return mergedTranscriptions;
      });
    };

    startRecordingEventSource.current.onerror = (event: Event) => {
      // Only handle errors when the connection is actually closed
      // EventSource fires error events during reconnection attempts, which is normal
      console.error(`Recording stream connection closed: ${url}`);
      setConnectionLostOpen(true);
      if (startRecordingEventSource.current?.readyState === EventSource.CLOSED || startRecordingEventSource.current?.readyState === EventSource.CONNECTING) {
        if (agendaStreamEventSource.current) {
          agendaStreamEventSource.current.close();
        }
        if (startRecordingEventSource.current) {
          startRecordingEventSource.current.close();
        }
      }

      if (startRecordingEventSource.current) {
        handleError(event, meetingId, startRecordingEventSource.current);
      }

      setLoading(false);
    };

    activeEventSources.set(key, startRecordingEventSource.current);
  };

  const handleAddAgendaStart = (index: number, status: string, title?: string) => {
    setAgendaStart(true);
    setIsAgendaBroadcasting(true);
    if (!agendaStart) {
      localStorage.setItem(`agendaStart_${mergedMeetingId}`, "true");

      // Get the speaker at this index to use its sequence number
      const speakerAtIndex = transcriptions[index];
      let fixedOrderIndex: number;

      if (speakerAtIndex && speakerAtIndex.type === "speaker") {
        // Use the speaker's sequence number for correct positioning
        // This ensures the agenda appears at the exact speaker's position even after sorting
        fixedOrderIndex = speakerAtIndex.sequence ?? index;
      } else {
        // Fallback to index if speaker not found
        fixedOrderIndex = index;
      }

      const newAgenda: IAgenda = {
        type: "agenda",
        agenda_id: status === AGENDA.START ? Date.now() : 0,
        title: title || "",
        createdBy: userRole ? userRole : USER.USER_ROLE.ADMIN,
        summary: "",
        timestamp: new Date().toISOString(),
        isAgendaEditing: title ? false : true, // If title is provided, don't show as editing
        speakers: [],
        id: uuidv4(),
        status: { initial: status, final: "" },
      } as IAgenda & { __fixedOrderIndex?: number };
      (newAgenda as any).__fixedOrderIndex = fixedOrderIndex;
      const agendaDetailsCreateUrl = `${baseURL}agenda-detail/update`;
      const createMeetingAgendaDetailPayload = {
        meeting_id: uniqueMeetingId ? parseInt(uniqueMeetingId.toString()) : 0,
        id: meetingDbId ? parseInt(meetingDbId?.toString()) : 0,
        startAgendaId: newAgenda.id,
        agendaStart: true,
      };
      postAPiCall(agendaDetailsCreateUrl, createMeetingAgendaDetailPayload);
      localStorage.setItem(
        `startIndex_${mergedMeetingId}`,
        JSON.stringify(newAgenda.id)
      );
      setStartIndex(newAgenda.id);

      // Insert the agenda immediately at the correct position in transcriptions
      // This ensures it appears at the exact speaker position
      setTranscriptions((prev) => {
        const updated = [...prev];
        // Insert agenda at the index where the speaker is
        // The agenda will be positioned correctly by stableAgendaSort using __fixedOrderIndex
        updated.splice(index, 0, newAgenda);
        return stableAgendaSort(updated);
      });

      const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/CREATE`;
      const payload = {
        agenda_id: newAgenda.id,
        agenda: newAgenda,
        startIndex: index,
        endIndex: null,
        type: "AGENDA",
        createdBy: userRole,
      };
      postAPiCall(url, payload, function () {
        setIsAgendaBroadcasting(false);
      });
    }
  };

  useEffect(() => {
    if (agendaAtLastIndex) {
      console.log("")
    }
    clearLocalStorage();
    agendaRefs.current = {};
  }, [transcriptions]);

  const getMeetingAgendaDetails = () => {
    const url = `${baseURL}agenda-detail/get`;
    const payload = {
      meeting_id: uniqueMeetingId ? parseInt(uniqueMeetingId.toString()) : 0,
      id: meetingDbId ? parseInt(meetingDbId?.toString()) : 0,
    };
    postAPiCall(url, payload, (data: any) => {
      const agendaStart: boolean = !data?.agendaStart ? false : true;
      localStorage.setItem(`startIndex_${mergedMeetingId}`, data.startAgendaId);
      localStorage.setItem(
        `agendaStart_${mergedMeetingId}`,
        JSON.stringify(agendaStart)
      );
      setStartIndex(data?.startAgendaId);
      setAgendaStart(agendaStart);
    });
  };

  const handleCountryChange = (
    newValue: OptionType | null,
    index: number,
    agendaIndex?: number
  ) => {
    const newCountry = newValue?.label || undefined;
    const interventionLabelId = newValue?.value ? Number(newValue.value) : null;
    const interventionLabelName = newCountry;

    if (!newCountry) {
      return;
    }
    if (agendaIndex === undefined) {
      let transcription = transcriptions[index];
      if (isSpeaker(transcription)) {
        setTranscriptions((prev: any) =>
          stableAgendaSort(prev.map((t: any, i: any) =>
            i === index && isSpeaker(t)
              ? {
                ...t,
                country: newCountry,
                intervention_label_id: interventionLabelId,
                intervention_label_name: interventionLabelName
              }
              : t
          ))
        );

        transcription.country = newCountry;
        transcription.intervention_label_id = interventionLabelId;
        transcription.intervention_label_name = interventionLabelName;

        // Call API to assign intervention label to speaker
        if (transcription.speech_id && interventionLabelId) {
          assignInterventionLabelToSpeaker(transcription.speech_id, interventionLabelId);
        }

        const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/CREATE`;
        // setShowLoader(true);
        const payload = {
          speaker_id: transcription.id,
          speaker: transcription,
          endIndex: null,
          startIndex: index,
          type: "SPEAKER",
          createdBy: transcription?.createdBy
            ? transcription?.createdBy
            : userRole,
        };
        postAPiCall(url, payload);
        // setShowLoader(false);
      }
    } else {
      let agenda = transcriptions[agendaIndex];
      if (isAgenda(agenda)) {
        let speaker = agenda.speakers[index];
        if (newCountry && speaker !== undefined) {
          speaker.country = newCountry;
          speaker.intervention_label_id = interventionLabelId;
          speaker.intervention_label_name = interventionLabelName;

          // Call API to assign intervention label to speaker
          if (speaker.speech_id && interventionLabelId) {
            assignInterventionLabelToSpeaker(speaker.speech_id, interventionLabelId);
          }

          const updatedAgenda = {
            ...agenda,
            speakers: [
              ...agenda.speakers.slice(0, index),
              speaker,
              ...agenda.speakers.slice(index + 1),
            ],
          };
          const newTranscriptions = [
            ...transcriptions.slice(0, agendaIndex),
            updatedAgenda,
            ...transcriptions.slice(agendaIndex + 1),
          ];
          setTranscriptions(stableAgendaSort(newTranscriptions));

          const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
          const payload = {
            agenda_id: agenda.id,
            agenda: agenda,
            endIndex: null,
            startIndex: agendaIndex,
            type: "AGENDA",
            createdBy: agenda.createdBy ? agenda.createdBy : userRole,
          };
          postAPiCall(url, payload);
        }
      }
    }
  };

  const handleSummaryEdit = (index: number, agendaIndex?: number) => {
    setTranscriptions((prev) => {
      if (agendaIndex === undefined) {
        // Standalone Speaker
        return stableAgendaSort(prev.map((t, i) =>
          i === index && t.type === "speaker"
            ? { ...t, isCountryEditing: true }
            : t
        ));
      } else {
        // Speaker inside Agenda
        const agenda = prev[agendaIndex];
        if (agenda.type === "agenda") {
          const updatedSpeakers = agenda.speakers.map((speaker, i) =>
            i === index ? { ...speaker, isCountryEditing: true } : speaker
          );
          return stableAgendaSort(prev.map((t, i) =>
            i === agendaIndex ? { ...t, speakers: updatedSpeakers } : t
          ));
        }
      }
      return prev; // No changes for other cases
    });
  };

  const handleCopy = (text: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => { })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    } else {
      console.error("Clipboard API is not available");
    }
  };

  const toggleTranscriptionShowMore = (
    agendaid: string | null,
    speakerId: string | null | undefined | number,
    action: string
  ) => {
    const isExpandedValue =
      action === AGENDA_DATA.SUMMARY_SHOW_TYPE.EXPAND ? true : false;

    if (speakerId) {
      if (agendaid) {
        // NOTE:: If speakerId present it means it is not agenda summary it is inteventian summary and if agendaid present means it is intervention summary under agenda (speker inside agenda) here we used agendaid to get specific agenda and used speakerId to get perticuler speker and changed isExpanded based on isExpandedValue which dicide in what you click
        setTranscriptions((prevTranscriptions) =>
          stableAgendaSort(prevTranscriptions.map((item) =>
            isAgenda(item) && item.id === agendaid
              ? {
                ...item,
                speakers: item.speakers.map((speaker) =>
                  speaker.speech_id === speakerId
                    ? { ...speaker, isExpanded: isExpandedValue }
                    : speaker
                ),
              }
              : item
          ))
        );
      } else {
        // NOTE:: If speakerId present it means it is not agenda summary it is inteventian summary and if agendaid not present means it is intervention summary of standalone speker here we used speakerId to get perticuler speker and changed isExpanded based on isExpandedValue which dicide in what you click
        setTranscriptions((prevTranscriptions) =>
          stableAgendaSort(prevTranscriptions.map((item) =>
            isSpeaker(item) && item.speech_id === speakerId
              ? {
                ...item,
                isExpanded: isExpandedValue,
              }
              : item
          ))
        );
      }
    } else {
      // NOTE:: If speakerId not present it means it is agenda summary we get perticuler agenda using agendaid and changed isExpanded value based on isExpandedValue which dicide in what you click
      setTranscriptions((prevTranscriptions) =>
        stableAgendaSort(prevTranscriptions.map((item) =>
          isAgenda(item) && item.id === agendaid
            ? {
              ...item,
              isExpanded: isExpandedValue,
            }
            : item
        ))
      );
    }
  };

  const handleAddAgendaEnd = (index: number, status: string) => {
    const start_index = transcriptions.findIndex(
      (ele) => ele.id === startIndex
    );
    const agenda = transcriptions[start_index];
    if (isAgenda(agenda) && agenda.title !== "") {
      handlePauseStopAgenda(index, status);
      setAgendaStart(false);
      localStorage.setItem(`agendaStart_${mergedMeetingId}`, "false");
    }
  };

  const handlePauseStopAgenda = (
    endIndex: number,
    status: string,
    isAgendaStream?: boolean,
    streamingStartIndex?: number
  ) => {
    setIsAgendaBroadcasting(true);
    try {
      const start_id =
        isAgendaStream && streamingStartIndex !== undefined
          ? streamingStartIndex
          : startIndex;

      // CRITICAL: Use functional update to ensure we always work with the latest transcriptions state
      // This prevents missing speakers that might have been added between agenda start and end
      setTranscriptions((currentTranscriptions) => {
        // Find agenda start index using latest state
        const start_index = currentTranscriptions.findIndex(
          (ele) => ele.id === start_id
        );

        if (start_index === -1 || endIndex === -1 || start_index >= endIndex) {
          console.warn(`⚠️ Invalid indices: start_index=${start_index}, endIndex=${endIndex}`);
          return currentTranscriptions; // Return unchanged if invalid
        }

        const agenda = currentTranscriptions[start_index];
        if (!isAgenda(agenda) || !agenda.title) {
          console.warn(`⚠️ Agenda not found or invalid at index ${start_index}`);
          return currentTranscriptions; // Return unchanged if agenda invalid
        }

        // CRITICAL: Validate endIndex against current state to ensure it's valid
        // endIndex might be stale, so we need to ensure it's within bounds
        const validEndIndex = Math.min(endIndex, currentTranscriptions.length - 1);
        if (validEndIndex < start_index) {
          console.warn(`⚠️ Invalid endIndex ${endIndex}, using ${validEndIndex}`);
        }

        // SIMPLE LOGIC: Include ALL speakers between start_index and validEndIndex in the agenda
        // Speakers AFTER validEndIndex remain standalone (whether manually added or not)
        const speakersBetween = currentTranscriptions.slice(
          start_index + 1,
          validEndIndex + 1  // validEndIndex is inclusive - this is the last speaker to include
        ) as Speaker[];

        // Include ALL speakers between start and end in the agenda
        // This includes both regular and manually added speakers if they're in the range
        const agendaSpeakers: Speaker[] = [];

        speakersBetween.forEach((item) => {
          if (item.type === "speaker") {
            // Include ALL speakers that are between agenda start and end
            agendaSpeakers.push(item);
          }
        });

        // Collect speech_ids of speakers that will be in the agenda
        // CRITICAL: Only track by speech_id - multiple speeches can share the same id
        const agendaSpeakerSpeechIds = new Set<string>();
        agendaSpeakers.forEach((speaker) => {
          if (speaker.speech_id) {
            agendaSpeakerSpeechIds.add(speaker.speech_id);
          }
        });

        console.log(`📋 Agenda finalization:`, {
          start_index,
          endIndex,
          validEndIndex,
          totalTranscriptions: currentTranscriptions.length,
          speakersInRange: speakersBetween.length,
          speakersInAgenda: agendaSpeakers.length,
          speakersBefore: currentTranscriptions.slice(0, start_index).filter(item => item.type === "speaker").length,
          speakersAfter: currentTranscriptions.slice(validEndIndex + 1).filter(item => item.type === "speaker").length,
          agendaSpeakerSpeechIds: Array.from(agendaSpeakerSpeechIds)
        });

        const newAgenda = { ...currentTranscriptions[start_index] };
        if (isAgenda(newAgenda)) {
          // CRITICAL: Ensure agenda has a valid ID for tracking
          if (!newAgenda.id) {
            console.error(`❌ Agenda at index ${start_index} has no ID!`);
            return currentTranscriptions; // Return unchanged if agenda has no ID
          }

          // CRITICAL: Preserve agenda_id from existing agenda (this is the real one from Redis/backend)
          // Don't overwrite it if it already exists - it's the database ID stored in Redis
          const existingAgendaId = newAgenda.agenda_id;
          if (existingAgendaId) {
            console.log(`📋 Preserving existing agenda_id ${existingAgendaId} for agenda ${newAgenda.id}`);
          }

          // Include all speakers between start and end (both regular and manually added)
          newAgenda.speakers = agendaSpeakers;
          newAgenda.status.final = status;

          // Ensure agenda_id is preserved (don't overwrite if it exists)
          if (existingAgendaId) {
            newAgenda.agenda_id = existingAgendaId;
          }

          // CRITICAL: Set __fixedOrderIndex based on the first speaker's sequence, not array index
          // This ensures proper ordering after refresh
          if (!(newAgenda as any).__fixedOrderIndex) {
            if (agendaSpeakers.length > 0 && agendaSpeakers[0].sequence != null) {
              (newAgenda as any).__fixedOrderIndex = agendaSpeakers[0].sequence;
              newAgenda.sequence = agendaSpeakers[0].sequence;
            } else {
              // Fallback to start_index if no sequence available
              (newAgenda as any).__fixedOrderIndex = start_index;
            }
          }

          // Log agenda ID for debugging
          console.log(`📋 Agenda ID: ${newAgenda.id}, Title: ${newAgenda.title}`);

          console.log(`📋 Finalizing agenda: ${newAgenda.title}`, {
            speakersInAgenda: agendaSpeakers.length,
            manuallyAddedIncluded: agendaSpeakers.filter(s => s.is_manually_added).length,
            status: status
          });

          // Build new transcriptions array:
          // 1. Everything before the agenda (PRESERVE ALL - these remain standalone)
          // 2. The agenda itself (with all speakers between start and end)
          // 3. Everything after validEndIndex (PRESERVE ALL - these remain standalone)
          const beforeAgenda = currentTranscriptions.slice(0, start_index);
          // CRITICAL: validEndIndex + 1 is the first item AFTER the last included speaker
          // All speakers after validEndIndex remain standalone (whether manually added or not)
          const afterEnd = currentTranscriptions.slice(validEndIndex + 1);

          // CRITICAL: Before building final array, verify that speakers in beforeAgenda and afterEnd
          // are NOT in the agenda's speakers array - this prevents incorrect deduplication
          const beforeAgendaSpeakers = beforeAgenda.filter(item => item.type === "speaker") as Speaker[];
          const afterEndSpeakers = afterEnd.filter(item => item.type === "speaker") as Speaker[];

          // Verify no speakers in beforeAgenda or afterEnd are in the agenda
          // CRITICAL: Only check by speech_id - multiple speeches can share the same id
          const speakersInBeforeThatShouldBeInAgenda = beforeAgendaSpeakers.filter(s =>
            s.speech_id && agendaSpeakerSpeechIds.has(s.speech_id)
          );
          const speakersInAfterThatShouldBeInAgenda = afterEndSpeakers.filter(s =>
            s.speech_id && agendaSpeakerSpeechIds.has(s.speech_id)
          );

          if (speakersInBeforeThatShouldBeInAgenda.length > 0 || speakersInAfterThatShouldBeInAgenda.length > 0) {
            console.warn(`⚠️ Found speakers that should be in agenda but are in before/after:`, {
              inBefore: speakersInBeforeThatShouldBeInAgenda.map(s => s.speech_id),
              inAfter: speakersInAfterThatShouldBeInAgenda.map(s => s.speech_id)
            });
          }

          console.log(`📋 Building transcriptions:`, {
            beforeAgenda: beforeAgenda.length,
            beforeAgendaSpeakers: beforeAgendaSpeakers.length,
            agendaSpeakers: agendaSpeakers.length,
            afterEnd: afterEnd.length,
            afterEndSpeakers: afterEndSpeakers.length,
            speakersInBeforeThatShouldBeInAgenda: speakersInBeforeThatShouldBeInAgenda.length,
            speakersInAfterThatShouldBeInAgenda: speakersInAfterThatShouldBeInAgenda.length
          });

          const newTranscriptions = [
            ...beforeAgenda,  // ALL speakers before agenda preserved
            newAgenda,
            ...afterEnd  // ALL speakers after validEndIndex preserved
          ];

          // CRITICAL: Use stableAgendaSort to ensure proper ordering while preserving all speakers
          // The deduplication inside stableAgendaSort will only remove speakers that are actually in the agenda
          const sortedTranscriptions = stableAgendaSort(newTranscriptions);

          // CRITICAL: Verify that all speakers are preserved after sorting
          // Expected: speakers before + speakers after = standalone speakers after sort
          const expectedStandaloneSpeakers = beforeAgendaSpeakers.length + afterEndSpeakers.length;
          const totalSpeakersAfterSort = sortedTranscriptions.filter(item => item.type === "speaker").length;

          // Count speakers in ALL agendas (may have duplicates across agendas, so count unique)
          const allSpeakersInAgendas = new Set<string>();
          sortedTranscriptions
            .filter(item => item.type === "agenda")
            .forEach(agendaItem => {
              const agenda = agendaItem as IAgenda;
              if (agenda.speakers && Array.isArray(agenda.speakers)) {
                agenda.speakers.forEach(speaker => {
                  if (speaker.speech_id) {
                    allSpeakersInAgendas.add(speaker.speech_id);
                  }
                });
              }
            });
          const speakersInAllAgendasAfterSort = allSpeakersInAgendas.size;

          // Count actual standalone speakers (not in any agenda)
          const actualStandaloneSpeakers = sortedTranscriptions.filter(item => {
            if (item.type === "speaker") {
              const speaker = item as Speaker;
              return !allSpeakersInAgendas.has(speaker.speech_id || '');
            }
            return false;
          }).length;

          console.log(`📋 Agenda finalized:`, {
            title: newAgenda.title,
            agendaId: newAgenda.id,
            speakersInAgenda: newAgenda.speakers.length,
            manuallyAddedIncluded: newAgenda.speakers.filter(s => s.is_manually_added).length,
            totalBeforeSort: newTranscriptions.length,
            totalAfterSort: sortedTranscriptions.length,
            expectedStandaloneSpeakers,
            totalSpeakersAfterSort,
            speakersInAllAgendasAfterSort,
            actualStandaloneSpeakers,
            allPreserved: expectedStandaloneSpeakers === actualStandaloneSpeakers
          });

          if (expectedStandaloneSpeakers !== actualStandaloneSpeakers) {
            console.error(`❌ CRITICAL: Speaker count mismatch!`, {
              expectedStandalone: expectedStandaloneSpeakers,
              actualStandalone: actualStandaloneSpeakers,
              totalSpeakersAfterSort,
              speakersInAllAgendas: speakersInAllAgendasAfterSort,
              speakersBeforeAgenda: beforeAgendaSpeakers.length,
              speakersAfterAgenda: afterEndSpeakers.length,
              speakersInThisAgenda: newAgenda.speakers.length
            });

            // If there's a mismatch, try to identify which speakers are missing
            const beforeSpeechIds = beforeAgendaSpeakers.map(s => s.speech_id).filter((id): id is string => Boolean(id));
            const afterSpeechIds = afterEndSpeakers.map(s => s.speech_id).filter((id): id is string => Boolean(id));
            const allExpectedStandaloneIdsArray = [...beforeSpeechIds, ...afterSpeechIds];
            const allExpectedStandaloneIds = new Set(allExpectedStandaloneIdsArray);

            const actualStandaloneAfterSort = sortedTranscriptions.filter(item => {
              if (item.type === "speaker") {
                const speaker = item as Speaker;
                // Check if this speaker is in any agenda
                // CRITICAL: Only check by speech_id - multiple speeches can share the same id
                const isInAgenda = sortedTranscriptions.some(agendaItem => {
                  if (agendaItem.type === "agenda") {
                    const agenda = agendaItem as IAgenda;
                    return agenda.speakers?.some(s =>
                      s.speech_id === speaker.speech_id
                    );
                  }
                  return false;
                });
                return !isInAgenda;
              }
              return false;
            });

            const actualStandaloneIds = new Set(actualStandaloneAfterSort.map(s => (s as Speaker).speech_id).filter((id): id is string => Boolean(id)));
            const missingIds = Array.from(allExpectedStandaloneIds).filter(id => !actualStandaloneIds.has(id));
            const extraIds = Array.from(actualStandaloneIds).filter(id => !allExpectedStandaloneIds.has(id));

            if (missingIds.length > 0) {
              console.error(`❌ Missing standalone speakers:`, missingIds);
            }
            if (extraIds.length > 0) {
              console.warn(`⚠️ Extra standalone speakers (not expected):`, extraIds);
            }
          }

          // Update ref immediately to ensure next operations use latest state
          transcriptionsRef.current = sortedTranscriptions;

          return sortedTranscriptions;
        }

        return currentTranscriptions; // Return unchanged if agenda processing failed
      });

      // Handle post-finalization logic after state update
      // Use setTimeout to ensure state update has been processed
      setTimeout(() => {
        // Get the latest transcriptions to find agenda index
        const latestTranscriptions = transcriptionsRef.current || transcriptions;
        const agendaIndex = latestTranscriptions.findIndex(
          (item) => item.type === "agenda" && item.id === start_id
        );

        if (agendaIndex === -1) {
          console.warn(`⚠️ Could not find agenda ${start_id} after finalization`);
          setIsAgendaBroadcasting(false);
          return;
        }

        const finalizedAgenda = latestTranscriptions[agendaIndex];
        if (!isAgenda(finalizedAgenda)) {
          console.warn(`⚠️ Item at index ${agendaIndex} is not an agenda`);
          setIsAgendaBroadcasting(false);
          return;
        }

        if (!isAgendaStream) {
          setCurrentAgenda(null);
          localStorage.setItem(`currentAgenda_${mergedMeetingId}`, "null");
          const agendaDetailsCreateUrl = `${baseURL}agenda-detail/update`;
          const payload = {
            meeting_id: uniqueMeetingId
              ? parseInt(uniqueMeetingId.toString())
              : 0,
            id: meetingDbId ? parseInt(meetingDbId?.toString()) : 0,
            startAgendaId: null,
            agendaStart: false,
          };
          postAPiCall(agendaDetailsCreateUrl, payload, () => {
            setStartIndex(0);
            localStorage.setItem(
              `startIndex_${mergedMeetingId}`,
              JSON.stringify(null)
            );
            setAgendaStart(false);
            localStorage.setItem(`agendaStart_${mergedMeetingId}`, "false");
          });

          if (status === AGENDA.STOP) {
            // Generate summary using the finalized agenda
            generateAgendaSummary(agendaIndex, finalizedAgenda, endIndex);
          } else {
            const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
            const payload = {
              agenda_id: finalizedAgenda.id,
              agenda: finalizedAgenda,
              endIndex: endIndex,
              startIndex: agendaIndex,
              type: "AGENDA",
              createdBy: finalizedAgenda.createdBy ? finalizedAgenda.createdBy : userRole,
            };
            postAPiCall(url, payload, function () {
              setIsAgendaBroadcasting(false);
            });
          }
        }

        // Scroll to agenda after a short delay
        setTimeout(() => {
          const agendaElement = agendaRefs.current[start_id];
          if (agendaElement) {
            agendaElement.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "nearest",
            });
          }
        }, 300);

        if (!isAgendaStream) {
          resetAgendaState();
        }
      }, 0);
    } catch (error) {
      console.error(error);
      setIsAgendaBroadcasting(false);
    }
  };

  const resetAgendaState = () => {
    setStartIndex(null);
    localStorage.setItem(`startIndex_${mergedMeetingId}`, "null");
  };

  const scrollToTranscript = (speech_id: string, country: string) => {
    const element = document.getElementById(`${speech_id}_${country}`);
    setIsUnderlined(true);
    requestAnimationFrame(() => {
      if (element) {
        const container = document.querySelector(".summary-section");
        if (container) {
          const yOffset = -50;
          const yPosition =
            element.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop +
            yOffset;

          container.scrollTo({ top: yPosition, behavior: "smooth" });

          element.classList.add("highlight");

          setTimeout(() => {
            element.classList.remove("highlight");
          }, 2000);
        } else {
          console.error("Scroll container not found.");
        }
      } else {
        console.error(`Element with id ${speech_id} not found.`);
      }
    });
  };

  const scrollToMiddleSection = (agenda_id: string) => {
    const element = document.getElementById(`${agenda_id}`);
    const container = document.querySelector(".summary-section") as HTMLElement;

    if (!element || !container) return;

    setIsUnderlined(true);
    requestAnimationFrame(() => {
      const offsetTop = element.offsetTop - 200;

      container.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    });

    element.classList.add("highlight");
    setTimeout(() => element.classList.remove("highlight"), 2000);
  };

  const updateTranscription = (transcriptions: Transcription[]) => {
    let updatedItems = [];
    updatedItems = [...transcriptions];
    setTranscriptions(stableAgendaSort(updatedItems));
  };

  const generateSummary = async (index: number, agendaIndex?: number) => {
    let transcription: Transcription;
    let speach = "";
    setIsAgendaBroadcasting(true);
    startApiCall();
    if (agendaIndex === null || agendaIndex === undefined) {
      transcription = transcriptions[index];
      if (isSpeaker(transcription)) {
        speach = transcription.text;
      }
      try {
        const response = await axios.post(
          `${baseURL}realtime-reporting/summary`,
          {
            value: speach,
          }
        );
        const data: SummaryData = response.data;
        if (data.summary) {
          setIsAgendaBroadcasting(false);
          setTranscriptions((prev) =>
            stableAgendaSort(prev.map((t, i) =>
              i === index && isSpeaker(t)
                ? {
                  ...t,
                  summaryTimestamp: new Date().toISOString(),
                  summary: data.summary,
                  isCountryEditing: true,
                  createdBy: userRole ? userRole : USER.USER_ROLE.ADMIN,
                }
                : t
            ))
          );
          // Navigate to the newly created intervention summary
          scrollToSummary(index);

          /**
           * Uncomment below lines incase of streaming as soon as summary is generated
           */
          /* transcription.summary = data.summary;
          const url = `${baseURL}realtime-streaming/stream/agenda/${uniqueMeetingId}/CREATE`;
              const payload = {
                speaker_id: transcription.id,
                speaker: transcription,
                endIndex: null,
                startIndex: index,
                type: "SPEAKER",
                createdBy: transcription.createdBy ? transcription.createdBy: nuserRole,
              };
              postAPiCall(url, payload, (response: any) => {
              }); */
        }

        // NOTE:: removed so not needed due to this on broudcasting overview not seen 26 1 2025
      } catch (error) {
        setIsAgendaBroadcasting(false);
        console.error("Error generating summary:", error);
      } finally {
        endApiCall();
      }
    } else {
      let agenda = transcriptions[agendaIndex];
      if (isAgenda(agenda)) {
        let speaker = agenda.speakers[index];
        speach = speaker.text;
        try {
          const response = await axios.post(
            `${baseURL}realtime-reporting/summary`,
            {
              value: speach,
            }
          );
          const data: SummaryData = response.data;
          if (data.summary) {
            setIsAgendaBroadcasting(false);
            speaker.summary = data.summary;
            if (isAgenda(transcriptions[agendaIndex])) {
              agenda.speakers[index].summary = speaker.summary;
              agenda.speakers[index].isCountryEditing = true;
              agenda.speakers[index].summaryTimestamp =
                new Date().toISOString();
              agenda.speakers[index].createdBy = userRole
                ? userRole
                : USER.USER_ROLE.ADMIN;
              let newTranscription = [
                ...transcriptions.slice(0, agendaIndex),
                agenda,
                ...transcriptions.slice(agendaIndex + 1),
              ];
              setTranscriptions([...newTranscription]);
              // Navigate to the newly created intervention summary in agenda
              // For agenda summaries, we need to find the index in the summary section
              // The summary for agenda speakers is rendered at the agenda index
              scrollToSummary(agendaIndex);

              /**
               * Uncomment below lines incase of streaming as soon as summary is generated
               */
              /* const url = `${baseURL}realtime-streaming/stream/agenda/${uniqueMeetingId}/UPDATE`;
              const payload = {
                agenda_id: agenda.id,
                agenda: agenda,
                endIndex: null,
                startIndex: agendaIndex,
                type: "AGENDA",
                createdBy: agenda.createdBy? agenda.createdB:userRole,
              };
              postAPiCall(url, payload, (response: any) => {
              }); */
            }
          }

          // setSummaryGenerated(true);
        } catch (error) {
          setIsAgendaBroadcasting(false);
        } finally {
          endApiCall();
        }
      }
    }
  };

  const updateAgendaTitle = (
    title: string,
    index: number,
    agenda_id?: number
  ) => {
    let agenda = { ...transcriptions[index] };
    if (isAgenda(agenda)) {
      agenda["title"] = title;
      if ((agenda_id !== null || agenda_id !== undefined) && agenda_id) {
        agenda["agenda_id"] = agenda_id;
      }
      const updatedTranscriptions = [...transcriptions];
      updatedTranscriptions[index] = agenda;
      updateTranscription(updatedTranscriptions);
    }
  };

  const findObjectsByAgendaId = (agenda_id: number, transcriptionsList?: Transcription[]) => {
    const listToSearch = transcriptionsList || transcriptions;
    return listToSearch.filter(
      (item) => isAgenda(item) && item.agenda_id === agenda_id
    );
  };

  /**
   * Regenerate agenda summary for a specific agenda
   * Checks that there are speakers with text before regenerating
   */
  const regenerateAgendaSummary = async (agendaId: string) => {
    startApiCall();
    try {
      setIsAgendaBroadcasting(true);

      // Use ref to get the latest transcriptions state
      const currentTranscriptions = transcriptionsRef.current;

      // Find the agenda in transcriptions
      const agendaIndex = currentTranscriptions.findIndex(
        (item) => item.type === "agenda" && item.id === agendaId
      );

      if (agendaIndex === -1) {
        console.error(`❌ Could not find agenda ${agendaId} in transcriptions`);
        setIsAgendaBroadcasting(false);
        return;
      }

      const agenda = currentTranscriptions[agendaIndex] as IAgenda;

      // Check if agenda has speakers
      if (!agenda.speakers || agenda.speakers.length === 0) {
        console.warn(`⚠️ Agenda ${agendaId} has no speakers, cannot regenerate summary`);
        setIsAgendaBroadcasting(false);
        return;
      }

      // Check that all speakers have text
      const speakersWithText = agenda.speakers.filter(
        (speaker: Speaker) => speaker.text && speaker.text.trim().length > 0
      );

      if (speakersWithText.length === 0) {
        console.warn(`⚠️ Agenda ${agendaId} has no speakers with text, cannot regenerate summary`);
        setIsAgendaBroadcasting(false);
        return;
      }

      console.log(`🔄 Regenerating agenda summary for agenda ${agendaId} with ${speakersWithText.length} speakers`);

      // Get all agendas with the same agenda_id (for summary generation)
      const agendas = findObjectsByAgendaId(agenda.agenda_id, currentTranscriptions);

      // Validate agendas have speakers with text
      const validAgendas = agendas.filter((item): item is IAgenda => {
        if (!isAgenda(item)) return false;
        const ag = item as IAgenda;
        if (!ag.speakers || ag.speakers.length === 0) return false;
        return ag.speakers.some((s: Speaker) => s.text && s.text.trim().length > 0);
      });

      if (validAgendas.length === 0) {
        console.warn(`⚠️ No valid agendas with speakers found for agenda_id ${agenda.agenda_id}`);
        setIsAgendaBroadcasting(false);
        return;
      }

      // Call the API to regenerate summary
      const response = await axios.post(
        `${baseURL}realtime-reporting/agenda-summary`,
        {
          value: validAgendas,
        }
      );

      if (response.data && response.data.summary) {
        // Update the agenda summary
        setTranscriptions((prev) => {
          const updated = [...prev];
          const currentAgendaIndex = updated.findIndex(
            (item) => item.type === "agenda" && item.id === agendaId
          );

          if (currentAgendaIndex !== -1) {
            const agendaToUpdate = updated[currentAgendaIndex] as IAgenda;
            if (isAgenda(agendaToUpdate)) {
              agendaToUpdate.summary = response.data.summary;
              agendaToUpdate.isCountryEditing = true;
              agendaToUpdate.summaryTimestamp = new Date().toISOString();

              // Update agenda in backend
              const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
              const payload = {
                agenda_id: agendaToUpdate.id,
                agenda: agendaToUpdate,
                endIndex: null,
                startIndex: currentAgendaIndex,
                type: "AGENDA",
                createdBy: agendaToUpdate.createdBy ? agendaToUpdate.createdBy : userRole,
              };
              postAPiCall(url, payload, () => {
                setIsAgendaBroadcasting(false);
              });

              return stableAgendaSort(updated);
            }
          }

          setIsAgendaBroadcasting(false);
          return prev;
        });
      } else {
        console.error("❌ Invalid response from agenda-summary API");
        setIsAgendaBroadcasting(false);
      }
    } catch (error) {
      setIsAgendaBroadcasting(false);
      console.error("Error regenerating agenda summary:", error);
    } finally {
      endApiCall();
    }
  };

  const generateAgendaSummary = async (
    _agendaIndex: number, // Unused - we find agenda by ID instead
    agenda: IAgenda,
    endIndex: number
  ) => {
    startApiCall();
    try {
      // Use functional update to get the latest transcriptions state
      // This ensures we have the agenda with manually added speakers excluded
      setTranscriptions((currentTranscriptions) => {
        // Find agenda by ID to ensure we have the correct index
        const currentAgendaIndex = currentTranscriptions.findIndex(
          (item) => item.type === "agenda" && item.id === agenda.id
        );

        if (currentAgendaIndex === -1) {
          console.error(`❌ Could not find agenda ${agenda.id} in transcriptions for summary generation`);
          setIsAgendaBroadcasting(false);
          return currentTranscriptions;
        }

        // Get agendas for summary generation using current state
        const agendas = findObjectsByAgendaId(agenda?.agenda_id, currentTranscriptions);

        // Generate summary (this is async)
        axios.post(
          `${baseURL}realtime-reporting/agenda-summary`,
          {
            value: agendas,
          }
        ).then((response) => {
          // Update state with summary after API call completes
          setTranscriptions((latestTranscriptions) => {
            const latestAgendaIndex = latestTranscriptions.findIndex(
              (item) => item.type === "agenda" && item.id === agenda.id
            );

            if (latestAgendaIndex !== -1) {
              const updatedItems = [...latestTranscriptions];
              const agendaToUpdate = updatedItems[latestAgendaIndex];

              if (isAgenda(agendaToUpdate)) {
                agendaToUpdate.summary = response.data.summary;
                agendaToUpdate.isCountryEditing = true;
                agendaToUpdate.summaryTimestamp = new Date().toISOString();

                // Also update the agenda object passed in
                agenda.summary = response.data.summary;
                agenda.summaryTimestamp = new Date().toISOString();
              }

              // Update agenda in transcriptions and send to backend
              const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
              const payload = {
                agenda_id: agenda.id,
                agenda: agenda,
                endIndex: endIndex,
                startIndex: latestAgendaIndex,
                type: "AGENDA",
                createdBy: agenda.createdBy ? agenda.createdBy : userRole,
              };
              postAPiCall(url, payload, function () {
                setIsAgendaBroadcasting(false);
              });

              return stableAgendaSort(updatedItems);
            } else {
              console.error(`❌ Could not find agenda ${agenda.id} in transcriptions after summary generation`);
              setIsAgendaBroadcasting(false);
              return latestTranscriptions;
            }
          });
        }).catch((error) => {
          setIsAgendaBroadcasting(false);
          console.error("Error generating agenda summary:", error);
        }).finally(() => {
          endApiCall();
        });

        // Return unchanged for now, will be updated in the then block
        return currentTranscriptions;
      });
    } catch (error) {
      setIsAgendaBroadcasting(false);
      console.error("Error generating agenda summary:", error);
      endApiCall();
    }
  };

  const handleTitleEditClick = (agendaIndex: number): void => {
    let agenda = { ...transcriptions[agendaIndex] };
    if (isAgenda(agenda)) {
      agenda.isAgendaEditing = true;
      // Store fixed order index before sorting
      if (!(agenda as any).__fixedOrderIndex) {
        (agenda as any).__fixedOrderIndex = agendaIndex;
      }
      const updatedTranscriptions = [...transcriptions];
      updatedTranscriptions[agendaIndex] = agenda;
      setTranscriptions(stableAgendaSort(updatedTranscriptions));

      /* const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/CREATE`;
      const payload = {
        agenda_id: agenda.id,
        agenda: agenda,
        startIndex: agendaIndex,
        endIndex: null,
        type: "AGENDA",
        createdBy: agenda.createdBy ? agenda.createdBy : userRole,
      };
      postAPiCall(url, payload); */
    }
  };

  const handleTitleSaveClick = (
    agendaIndex: number,
    selectedAgenda: any
  ): void => {
    setSelectedValue(null);
    let agenda = { ...transcriptions[agendaIndex] };
    if (isAgenda(agenda)) {
      if (agenda.status.initial === AGENDA.START) {
        // Check for duplicate title, but exclude the current agenda being edited
        // Compare by agenda ID to exclude the current agenda from duplicate check
        const duplicateAgenda = agendasArray.findIndex(
          (ele) =>
            isAgenda(ele) &&
            ele.title === selectedAgenda &&
            ele.id !== agenda.id // Exclude current agenda from duplicate check
        );
        if (duplicateAgenda !== -1) {
          dispatch({
            type: COMMON.SHOW_SNACKBAR,
            payload: {
              message:
                "Agenda title already exists. Please provide unique title.",
              severity: COMMON.SNACKBAR_SEVERITY.ERROR,
            },
          });
          return;
        }
        agenda.title = selectedAgenda;
      } else if (agenda.status.initial === AGENDA.RESUME) {
        agenda.title = selectedAgenda.label;
        agenda.agenda_id = selectedAgenda.value;
      }
      agenda.isAgendaEditing = false;
      agenda.timestamp = new Date().toISOString();
      const updatedTranscriptions = [...transcriptions];
      updatedTranscriptions[agendaIndex] = agenda;

      updatedTranscriptions.map((agendaObj: Transcription, index) => {
        if (
          isAgenda(agendaObj) &&
          agendaObj.title &&
          agendaObj.type === "agenda" &&
          isAgenda(agenda) &&
          agendaObj.agenda_id === agenda.agenda_id &&
          agendaObj.status.initial === AGENDA.RESUME
        ) {
          agendaObj.title = agenda.title;
          const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;

          const payload = {
            agenda_id: agendaObj.id,
            agenda: agendaObj,
            endIndex: null,
            startIndex: index,
            type: "AGENDA",
            createdBy: agendaObj.createdBy ? agendaObj.createdBy : userRole,
          };
          postAPiCall(url, payload);
        }
      });

      setTranscriptions(stableAgendaSort(updatedTranscriptions));

      const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
      transcriptions[agendaIndex].isCountryEditing = true;
      const payload = {
        agenda_id: agenda.id,
        agenda: agenda,
        endIndex: null,
        startIndex: agendaIndex,
        type: "AGENDA",
        createdBy: agenda.createdBy ? agenda.createdBy : userRole,
      };
      postAPiCall(url, payload);
    }
  };

  const handleJumpCurrent = () => {
    const container = document.querySelector(".transcription-container");
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const fetchAgendaById = (agenda_id: number) => {
    return agendasArray?.find(
      (item) => item?.type === "agenda" && item?.agenda_id === agenda_id
    ) as IAgenda | undefined;
  };

  const handleAgendaTitleChange = (
    newValue: AgendaOptionType | null,
    agenda: IAgenda,
    index: number
  ) => {
    if (newValue) {
      const pausedAgenda = fetchAgendaById(newValue.value);
      if (pausedAgenda !== undefined && pausedAgenda !== null) {
        agenda.agenda_id = pausedAgenda.agenda_id;
        const updatedAgenda: IAgenda = {
          ...agenda,
          title: newValue.label,
          agenda_id: pausedAgenda.agenda_id,
        };
        if (
          isAgenda(updatedAgenda) &&
          updatedAgenda !== undefined &&
          updatedAgenda !== null
        ) {
          const updatedTranscriptions = [...transcriptions];
          updatedTranscriptions[index] = updatedAgenda;
          updateTranscription(updatedTranscriptions);
          setSelectedValue(newValue);
        }
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case AGENDA.START:
        return "#034afc";
      case AGENDA.PAUSE:
        return "#ec8a13";
      case AGENDA.RESUME:
        return "#8621b9";
      case AGENDA.STOP:
        return "#eb130c";
      default:
        return "black"; // Default color if status doesn't match any case
    }
  };

  const handleOpenEditSummModal = (
    index: any,
    agndaIndex: any,
    initialSumm: any,
    summTitle: any,
    editSummaryType: any
  ) => {
    setEditSummary(initialSumm);
    setEditSummaryTitle(summTitle);
    setEditSummIndex(index);
    setEditSummAgendaIndex(agndaIndex);
    setEditSummaryType(editSummaryType);
    //fetchCountries();
    setEditSummModalOpen(true);
  };
  const handleEditSummModalClose = () => {
    setEditSummModalOpen(false);
  };

  const handleUpdateSummary = (updatedSummaryData: string, title: string, interventionLabelId?: number) => {
    // Find the intervention label ID from the title if not provided
    let labelId: number | undefined = interventionLabelId;
    if (!labelId && title) {
      const selectedOption = countryOptions.find(opt => opt.label === title);
      labelId = selectedOption ? Number(selectedOption.value) : undefined;
    }

    if (editSummaryType === AGENDA_DATA.EDIT_SUMMARY_TYPE.AGENDA_SUMMARY) {
      // NOTE:: Edit agenda summary based on editSummaryType
      if (editSummIndex !== undefined && editSummIndex !== null) {
        let agenda = { ...transcriptions[editSummIndex] };
        if (isAgenda(agenda)) {
          transcriptions.forEach((item, index) => {
            if (
              isAgenda(item) &&
              isAgenda(agenda) &&
              item?.agenda_id === agenda?.agenda_id
            ) {
              let agendaObj = { ...item };

              //if (index === editSummIndex) {
              // NOTE:: Edit agenda summary and title if matches to specific array index and oldTitle and broadcast the agenda
              if (isAgenda(agenda)) {
                agendaObj["title"] = title;
                if (index === editSummIndex) {
                  agendaObj["summary"] = updatedSummaryData;
                  agendaObj["summaryTimestamp"] = new Date().toISOString();
                }
                const payload = {
                  agenda_id: agendaObj.id,
                  agenda: agendaObj,
                  endIndex: null,
                  startIndex: index === editSummIndex ? editSummIndex : index,
                  type: "AGENDA",
                  createdBy: agendaObj.createdBy
                    ? agendaObj.createdBy
                    : userRole,
                };
                const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
                postAPiCall(url, payload, () => {
                  setEditSummModalOpen(false);
                });
              }
            }
          });
        }
      }
    } else {
      if (editSummAgendaIndex === undefined || editSummAgendaIndex === null) {
        if (editSummIndex !== undefined && editSummIndex !== null) {
          let transcription = transcriptions?.[editSummIndex];
          if (isSpeaker(transcription)) {
            transcription.summary = updatedSummaryData;
            transcription.country = title;
            transcription.intervention_label_id = labelId ?? undefined;
            transcription.intervention_label_name = title;
            transcription.summaryTimestamp = new Date().toISOString();
          }
          if (isSpeaker(transcription)) {
            setTranscriptions((prev) =>
              stableAgendaSort(prev.map((t, i) =>
                i === editSummIndex && isSpeaker(t)
                  ? {
                    ...t,
                    summary: updatedSummaryData,
                    country: title,
                    intervention_label_id: labelId,
                    intervention_label_name: title,
                    summaryTimestamp: new Date().toISOString(),
                  }
                  : t
              ))
            );

            // Update intervention label via API
            if (transcription.speech_id) {
              if (labelId) {
                assignInterventionLabelToSpeaker(transcription.speech_id, labelId);
              } else {
                // If label is removed, remove it from speaker
                removeInterventionLabelFromSpeaker(transcription.speech_id);
                // Update UI to show "Speaker"
                setTranscriptions((prev) =>
                  stableAgendaSort(prev.map((t, i) =>
                    i === editSummIndex && isSpeaker(t)
                      ? {
                        ...t,
                        intervention_label_id: null,
                        intervention_label_name: null,
                        country: undefined,
                      }
                      : t
                  ))
                );
              }

              // Call backend endpoint to persist intervention summary update
              startApiCall();
              const updateUrl = `${baseURL}${RestfulUrls.UPDATE_INTERVENTION_SUMMARY}`;
              axios.put(updateUrl, {
                speech_id: transcription.speech_id,
                summary_text: updatedSummaryData,
                intervention_label_id: labelId,
                intervention_label_name: title,
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem(COMMON.ACCESS_TOKEN)}`
                }
              }).catch((error) => {
                console.error("Error updating intervention summary via API:", error);
                // Continue with Redis update even if API call fails
              }).finally(() => {
                endApiCall();
              });
            }

            const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
            const payload = {
              speaker_id: transcription.id,
              speaker: transcription,
              endIndex: null,
              startIndex: editSummIndex,
              type: "SPEAKER",
              createdBy: transcription?.createdBy
                ? transcription?.createdBy
                : userRole,
            };
            postAPiCall(url, payload, () => {
              setEditSummModalOpen(false);
            });
          }
        }
      } else {
        let agenda = transcriptions[editSummAgendaIndex];
        if (
          isAgenda(agenda) &&
          editSummIndex !== undefined &&
          editSummIndex !== null
        ) {
          let speaker = agenda.speakers[editSummIndex];
          if (updatedSummaryData) {
            speaker.summary = updatedSummaryData;
            speaker.country = title;
            speaker.intervention_label_id = labelId;
            speaker.intervention_label_name = title;
            speaker.summaryTimestamp = new Date().toISOString();

            // Update intervention label via API
            if (speaker.speech_id) {
              if (labelId) {
                assignInterventionLabelToSpeaker(speaker.speech_id, labelId);
              } else {
                removeInterventionLabelFromSpeaker(speaker.speech_id);
              }
            }
            const updatedAgenda = {
              ...agenda,
              speakers: [
                ...agenda.speakers.slice(0, editSummIndex),
                speaker,
                ...agenda.speakers.slice(editSummIndex + 1),
              ],
            };
            const newTranscriptions = [
              ...transcriptions.slice(0, editSummAgendaIndex),
              updatedAgenda,
              ...transcriptions.slice(editSummAgendaIndex + 1),
            ];
            setTranscriptions(stableAgendaSort(newTranscriptions));

            const url = `${baseURL}realtime-streaming/stream/agenda/${mergedMeetingId}/UPDATE`;
            const payload = {
              agenda_id: agenda.id,
              agenda: updatedAgenda, // Ensure the updated agenda is passed
              endIndex: null,
              startIndex: editSummAgendaIndex,
              type: "AGENDA",
              createdBy: updatedAgenda.createdBy
                ? updatedAgenda.createdBy
                : userRole,
            };
            postAPiCall(url, payload, () => {
              setEditSummModalOpen(false);
            });
          }
        }
      }
    }
  };

  // Single pass to get all required agenda information
  const { agendasArray, agendaAtLastIndex, activeAgendaIndex, successiveAgendaStartIndex } = transcriptions.reduce(
    (acc, item, index) => {
      if (isAgenda(item)) {
        acc.agendasArray.push(item);

        // Check for agenda at last index (with empty final status)
        if (item.status?.final === "") {
          acc.agendaAtLastIndex = item;
        }

        // Check for active agenda
        if (item.id === currentAgenda?.id && item.status?.final === "") {
          acc.activeAgendaIndex = index;
        }

        // Check for successive agenda start
        if (acc.activeAgendaIndex > -1 && index > acc.activeAgendaIndex && item.status?.initial === AGENDA.START) {
          acc.successiveAgendaStartIndex = index;
        }
      }
      return acc;
    },
    {
      agendasArray: [] as IAgenda[],
      agendaAtLastIndex: null as IAgenda | null,
      activeAgendaIndex: -1,
      successiveAgendaStartIndex: -1
    }
  );

  // Filter and map to get options for agendas that are currently active (i.e., can be paused, resumed, or closed)
  const agendaOptions = transcriptions
    .filter(isAgenda) // Ensure the item is an agenda
    .filter((agenda: any) => {
      // Check if this specific agenda title has any 'STOP' status
      const hasStopped = agendasArray.some(
        (item: any) =>
          item?.agenda_id === agenda?.agenda_id &&
          item?.status?.final === AGENDA_DATA.AGENDA_STATUS.STOP
      );
      // Only include if the current agenda is paused and has not been stopped
      return (
        !hasStopped &&
        (agenda.status.initial === AGENDA_DATA.AGENDA_STATUS.START ||
          agenda.status.initial === AGENDA_DATA.AGENDA_STATUS.RESUME) &&
        agenda.status.final === AGENDA_DATA.AGENDA_STATUS.PAUSE
      );
    })
    .reduce((uniqueAgendas: any[], agenda: any) => {
      // Ensure the title is unique
      if (!uniqueAgendas.some((item: any) => item.label === agenda.title)) {
        uniqueAgendas.push({
          label: agenda.title,
          value: agenda.agenda_id,
        });
      }
      return uniqueAgendas;
    }, []);

  const scrollToText = (speech_id: string) => {
    const element = document.getElementById(speech_id);
    setIsUnderlined(true);

    if (!element) {
      console.error(`Element with id ${speech_id} not found.`);
      return;
    }

    requestAnimationFrame(() => {
      const container = document.querySelector(".transcription-container");
      if (container) {
        const yOffset = -50;
        const yPosition =
          element.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop +
          yOffset;

        container.scrollTo({ top: yPosition, behavior: "smooth" });
      } else {
        console.error("Scroll container not found.");
      }
    });
  };

  const dummyModalCloseTw = () => { };
  const summaryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Function to scroll to a summary by index
  const scrollToSummary = (index: number) => {
    // Use setTimeout to ensure DOM is updated after state change
    setTimeout(() => {
      const summaryElement = summaryRefs.current[index];
      if (summaryElement) {
        const container = document.querySelector(".summary-section");
        if (container) {
          const yOffset = -50;
          const yPosition =
            summaryElement.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop +
            yOffset;

          container.scrollTo({ top: yPosition, behavior: "smooth" });
        } else {
          // Fallback: try to scroll to the summary element directly
          summaryElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }, 100); // Small delay to ensure DOM is updated
  };

  const observerErr =
    "ResizeObserver loop completed with undelivered notifications";
  const suppressResizeObserverError = (e: {
    message: string;
    stopImmediatePropagation: () => void;
  }) => {
    if (e.message === observerErr) {
      e.stopImmediatePropagation();
    }
  };

  window.addEventListener("error", suppressResizeObserverError);

  return (
    <Box className="main-layout">
      {showLoader && (
        <LoaderWithText isOpen={showLoader} onClose={dummyModalCloseTw} />
      )}
      <Box className="left-layout">
        <Box className="transcription-container" ref={containerRef}>
          {transcriptions?.map((item, index) => {
            const key = item.type === "agenda" ? item.id : (item.speech_id || `speaker-${item.id || index}`);
            if (item.type === "agenda") {
              return (
                <Agenda
                  key={key}
                  agenda={item}
                  index={index}
                  selectedValue={selectedValue}
                  setSelectedValue={setSelectedValue}
                  getStatusColor={getStatusColor}
                  updateAgendaTitle={updateAgendaTitle}
                  handleTitleSaveClick={handleTitleSaveClick}
                  agendaOptions={agendaOptions}
                  handleAgendaTitleChange={handleAgendaTitleChange}
                  navigateSpeechId={navigateSpeechId}
                  handleTitleEditClick={handleTitleEditClick}
                  agendaStart={agendaStart}
                  generateSummary={generateSummary}
                  currentAgenda={currentAgenda}
                  handlePauseStopAgenda={handlePauseStopAgenda}
                  handleAddAgendaStart={handleAddAgendaStart}
                  handleAddAgendaEnd={handleAddAgendaEnd}
                  renderedSpeakers={renderedSpeakers}
                  agendaRefs={agendaRefs}
                  isAgendaBroadcasting={isAgendaBroadcasting}
                  transcriptions={transcriptions}
                  agendaStartIndex={activeAgendaIndex}
                  successiveAgendaStartIndex={successiveAgendaStartIndex}
                  handleEditSpeaker={handleOpenEditSpeakerModal}
                  handleDeleteSpeaker={handleOpenDeleteConfirm}
                  handleOpenAddSpeakerModal={handleOpenAddSpeakerModalForAgenda}
                  handleShouldShowActionButtonsAfterSpeakerTranscribed={shouldShowActionButtonsAfterSpeakerTranscribed}
                />
              );
            } else if (item.type === "speaker") {
              // Use actual array index (not filtered speaker list index)
              const actualIndex = index; // This is the actual index in transcriptions array
              const canAddSection = hasPermission(
                userPermissions,
                PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                PERMISSION?.ACTION?.MERGE_TRANSCRIPTION
              );

              return (
                <React.Fragment key={key}>
                  <StandaloneSpeaker
                    speaker={item}
                    index={index}
                    agendaStart={agendaStart}
                    navigateSpeechId={navigateSpeechId}
                    generateSummary={generateSummary}
                    currentAgenda={currentAgenda}
                    handlePauseStopAgenda={handlePauseStopAgenda}
                    handleAddAgendaStart={handleAddAgendaStart}
                    handleAddAgendaEnd={handleAddAgendaEnd}
                    agendaOptions={agendaOptions}
                    renderedSpeakers={renderedSpeakers}
                    isAgendaBroadcasting={isAgendaBroadcasting}
                    agendaStartIndex={activeAgendaIndex}
                    successiveAgendaStartIndex={successiveAgendaStartIndex}
                    handleEditSpeaker={handleOpenEditSpeakerModal}
                    handleDeleteSpeaker={handleOpenDeleteConfirm}
                    transcriptions={transcriptions}
                    actualIndex={actualIndex}
                    currentTime={currentTime}
                    handleShouldShowActionButtonsAfterSpeakerTranscribed={shouldShowActionButtonsAfterSpeakerTranscribed}
                  />
                  {canAddSection && shouldShowActionButtonsAfterSpeakerTranscribed(transcriptions, actualIndex, currentTime) && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 1,
                        position: "relative",
                      }}
                    >
                      <Typography
                        component="span"
                        onClick={() => handleOpenAddSpeakerModal(actualIndex)}
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontSize: "14px",
                          fontWeight: 500,
                          "&:hover": {
                            color: "primary.dark",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Add new speaker section
                      </Typography>
                    </Box>
                  )}
                </React.Fragment>
              );
            }
            return null;
          })}
        </Box>
        {transcriptions.length > 0 && (
          <Box className="jump-current">
            <Button
              variant="contained"
              className="scroll-bottom"
              onClick={handleJumpCurrent}
            >
              <TrackChangesOutlinedIcon /> Jump to Active
            </Button>
          </Box>
        )}
      </Box>

      <Box className="middle-layout">
        <Box className="summary-section" ref={containerRef}>
          {transcriptions.length === 0 ? (
            <Typography></Typography>
          ) : (
            transcriptions.map((transcription, index) => {
              const key = transcription.type === "agenda"
                ? transcription.id
                : (transcription.speech_id || `speaker-${transcription.id || index}`);
              return (
                <React.Fragment key={key}>
                  {/* Render summary for speakers */}
                  {isSpeaker(transcription) && (
                    <div ref={(el) => (summaryRefs.current[index] = el)}>
                      <Summary
                        speaker={transcription}
                        index={index}
                        scrollToText={scrollToText}
                        handleNavigateSpeechId={handleNavigateSpeechId}
                        countryOptions={countryOptions}
                        handleCountryChange={handleCountryChange}
                        handleCopy={handleCopy}
                        handleOpenEditSummModal={handleOpenEditSummModal}
                        handleSummaryEdit={handleSummaryEdit}
                        toggleTranscriptionShowMore={toggleTranscriptionShowMore}
                        handleDeleteInterventionSummary={handleOpenDeleteInterventionSummaryConfirm}
                      />
                    </div>
                  )}

                  {isAgenda(transcription) &&
                    // Only render summary for the last speaker in the agenda
                    transcription.speakers.map((speaker, speakerIndex) => {
                      const isLastSpeaker =
                        transcription?.speakers.length - 1 === speakerIndex;
                      const speakerKey = speaker.speech_id || `speaker-${speaker.id || speakerIndex}`;

                      return (
                        <React.Fragment key={speakerKey}>
                          <div
                            ref={
                              isLastSpeaker
                                ? (el) => (summaryRefs.current[index] = el)
                                : undefined
                            }
                          >
                            <Summary
                              speaker={speaker}
                              index={speakerIndex}
                              agendaIndex={index}
                              agendaID={transcription?.id}
                              scrollToText={scrollToText}
                              handleNavigateSpeechId={handleNavigateSpeechId}
                              countryOptions={countryOptions}
                              handleCountryChange={handleCountryChange}
                              handleCopy={handleCopy}
                              handleOpenEditSummModal={handleOpenEditSummModal}
                              handleSummaryEdit={handleSummaryEdit}
                              toggleTranscriptionShowMore={
                                toggleTranscriptionShowMore
                              }
                              handleDeleteInterventionSummary={handleOpenDeleteInterventionSummaryConfirm}
                            />
                          </div>
                          {/* Show agenda summary only for the last speaker */}
                          {isLastSpeaker && transcription.summary && (
                            <Box
                              key={index}
                              id={"agenda_" + transcription.id}
                              className="summary-container"
                            >
                              <Box className="country-option-container">
                                <Typography
                                  variant="body2"
                                  onClick={() => {
                                    if (transcription?.id && speaker.speech_id) {
                                      scrollToText(speaker?.speech_id);
                                      handleNavigateSpeechId(transcription.id);
                                    }
                                  }}
                                >
                                  <AppTitleTypography
                                    text={transcription?.title}
                                    maxLength={
                                      AGENDA_DATA.OVERVIEW_AGENDA_NAME_MAX_LENGTH
                                    }
                                    className=" typography-ellipsis sm-font14"
                                    variant="subtitle1"

                                  />
                                </Typography>
                                <Box className="icons-buttons">
                                  {hasPermission(
                                    userPermissions,
                                    PERMISSION?.MODULE.MEETING_TRANSCRIPTION_PAGE,
                                    PERMISSION?.ACTION?.COPY_SUMMARY
                                  ) && (
                                      <IconButton
                                        onClick={() =>
                                          handleCopy(
                                            transcription.title +
                                            " : " +
                                            transcription.summary || ""
                                          )
                                        }
                                        className="copy-icon"
                                      >
                                        <CopyIcon />
                                      </IconButton>
                                    )}
                                  {/* {userRole &&
                                userRole === USER.USER_ROLE.ADMIN ? (
                                  <EditIcon
                                    onClick={() => {
                                      handleOpenEditSummModal(
                                        index,
                                        speakerIndex,
                                        transcription.summary || "",
                                        transcription.title || "",
                                        AGENDA_DATA.EDIT_SUMMARY_TYPE
                                          .AGENDA_SUMMARY
                                      );
                                    }}
                                    sx={{
                                      fontSize: "13px",
                                      cursor: "pointer",
                                      ml: "5px",
                                    }}
                                  />
                                ) : null} */}
                                  {hasPermission(
                                    userPermissions,
                                    PERMISSION?.MODULE.MEETING_TRANSCRIPTION_PAGE,
                                    PERMISSION?.ACTION?.CREATE_AGENDA_SUMMARY
                                  ) && (
                                      <EditIcon
                                        onClick={() => {
                                          handleOpenEditSummModal(
                                            index,
                                            speakerIndex,
                                            transcription.summary || "",
                                            transcription.title || "",
                                            AGENDA_DATA.EDIT_SUMMARY_TYPE
                                              .AGENDA_SUMMARY
                                          );
                                        }}
                                        sx={{
                                          fontSize: "13px",
                                          cursor: "pointer",
                                          ml: "5px",
                                        }}
                                      />
                                    )}
                                  {/* Regenerate Summary button - always visible for admin/super admin */}
                                  {hasPermission(
                                    userPermissions,
                                    PERMISSION?.MODULE.MEETING_TRANSCRIPTION_PAGE,
                                    PERMISSION?.ACTION?.REGENERATE_AGENDA_SUMMARY
                                  ) && (
                                      <Tooltip title="Regenerate Agenda Item Summary">
                                        <IconButton
                                          onClick={() => {
                                            regenerateAgendaSummary(transcription.id);
                                          }}
                                          disabled={isAgendaBroadcasting}
                                          sx={{
                                            ml: "5px",
                                            padding: "4px",
                                            "&:hover": {
                                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                                            },
                                          }}
                                        >
                                          <RefreshIcon
                                            sx={{
                                              fontSize: "18px",
                                              color: isAgendaBroadcasting ? "gray" : "primary.main",
                                            }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                </Box>
                              </Box>

                              <Box
                                onClick={() => {
                                  if (transcription?.id && speaker.speech_id) {
                                    scrollToText(speaker?.speech_id);
                                    handleNavigateSpeechId(transcription?.id);
                                  }
                                }}
                              >
                                <SpeakerTimestamp
                                  timestamp={
                                    transcription?.summaryTimestamp
                                      ? transcription?.summaryTimestamp
                                      : transcription?.timestamp
                                  }
                                  timeClass="intervention-overview-time"
                                />
                              </Box>

                              {/* Summary Text */}
                              <Typography
                                variant="body2"
                                className="summary-text"
                                onClick={() => {
                                  if (transcription?.id && speaker.speech_id) {
                                    scrollToText(speaker?.speech_id);
                                    handleNavigateSpeechId(transcription.id);
                                  }
                                }}
                                title="Click to navigate to the transcription"
                              >
                                {transcription?.isExpanded
                                  ? transcription.summary ?? ""
                                  : `${(transcription.summary ?? "").slice(
                                    0,
                                    100
                                  )}${(transcription?.summary ?? "").length > 100
                                    ? "..."
                                    : ""
                                  }`}
                              </Typography>

                              {transcription?.isExpanded ? (
                                <Typography
                                  variant="body2"
                                  className="expand-showless"
                                  onClick={() =>
                                    toggleTranscriptionShowMore(
                                      transcription?.id,
                                      null,
                                      AGENDA_DATA.SUMMARY_SHOW_TYPE.SHOW_LESS
                                    )
                                  }
                                >
                                  Show Less
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  className="expand-showless"
                                  onClick={() =>
                                    toggleTranscriptionShowMore(
                                      transcription?.id,
                                      null,
                                      AGENDA_DATA.SUMMARY_SHOW_TYPE.EXPAND
                                    )
                                  }
                                >
                                  Expand
                                </Typography>
                              )}
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })
          )}
        </Box>
      </Box>

      <Box className="right-layout">
        <Typography className="overview-title">Overview</Typography>
        <Box className="right-panel">
          <Box className="summary-section-title">
            {transcriptions.map(
              (transcription: Transcription, index: number) => {
                const key = transcription.type === "agenda"
                  ? transcription.id
                  : (transcription.speech_id || `speaker-${transcription.id || index}`);
                return (
                  <Box key={key}>
                    {isSpeaker(transcription) &&
                      transcription?.country &&
                      transcription?.speech_id && (
                        <Box className="intervention-items">
                          <Typography
                            variant="body2"
                            className="summary-item"
                            sx={{
                              cursor: "pointer",
                              textDecoration: isUnderlined ? "underline" : "none",
                              color: "black",
                            }}
                            onClick={() => {
                              if (transcription?.speech_id) {
                                scrollToTranscript(
                                  transcription.speech_id,
                                  transcription?.country || ""
                                );
                                handleNavigateSpeechId(
                                  String(transcription.speech_id)
                                );
                              }
                            }}
                            component="div"
                          >
                            {transcription?.country}
                            <SpeakerTimestamp
                              timestamp={
                                transcription?.summaryTimestamp
                                  ? transcription?.summaryTimestamp
                                  : transcription?.timestamp
                              }
                              timeClass="intervention-overview-time"
                            />
                          </Typography>
                        </Box>
                      )}
                    {isAgenda(transcription) && (
                      <Box className="agenda-overview">
                        <div
                          style={{
                            backgroundColor: getStatusColor(
                              transcription?.status?.initial
                            ),
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const fallbackSpeaker = transcription?.speakers?.[0];
                            const speechId = fallbackSpeaker?.speech_id;

                            if (speechId) {
                              scrollToText(speechId);
                              handleNavigateSpeechId(String(speechId));
                            } else {
                              console.warn(
                                "No valid speech_id available for this agenda item."
                              );
                            }
                          }}
                        >
                          <AppTitleTypography
                            text={transcription?.title}
                            maxLength={
                              AGENDA_DATA.OVERVIEW_AGENDA_NAME_MAX_LENGTH
                            }
                            className="section-title typography-ellipsis"
                            variant="subtitle1"
                          />
                        </div>

                        {transcription.speakers &&
                          transcription?.speakers?.length > 0 && (
                            <Box className="agenda-speakers">
                              {transcription.speakers.map(
                                (speaker, speakerIndex) => {
                                  const speakerKey = speaker.speech_id || `speaker-${speaker.id || speakerIndex}`;
                                  return (
                                    <Box
                                      key={speakerKey}
                                      className="speaker-details"
                                    >
                                      {speaker.country && (
                                        <Typography
                                          variant="body2"
                                          className="summary-item"
                                          sx={{
                                            cursor: "pointer",
                                            textDecoration: isUnderlined
                                              ? "underline"
                                              : "none",
                                            color: "black",
                                          }}
                                          onClick={() =>
                                            speaker.speech_id &&
                                            scrollToTranscript(
                                              speaker?.speech_id,
                                              speaker?.country || ""
                                            )
                                          }
                                          component="div"
                                        >
                                          {speaker?.country}
                                          <SpeakerTimestamp
                                            timestamp={
                                              speaker?.summaryTimestamp
                                                ? speaker?.summaryTimestamp
                                                : speaker?.timestamp
                                            }
                                            timeClass="intervention-overview-time"
                                          />
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }
                              )}
                            </Box>
                          )}

                        {transcription?.status?.final && (
                          <Typography
                            component="a"
                            onClick={() => {
                              if (transcription?.id) {
                                const idToHighlight = `agenda_${transcription.id}`;
                                // setHighlightedId(idToHighlight);

                                // Call scroll function
                                scrollToMiddleSection(idToHighlight);

                                // Optional: auto-remove the React highlight state after 2 seconds
                                // setTimeout(() => {
                                //   setHighlightedId(null);
                                // }, 2000);
                              }
                            }}
                            sx={{
                              backgroundColor: getStatusColor(
                                transcription?.status?.final
                              ),
                              cursor:
                                transcription?.status?.final === AGENDA.PAUSE
                                  ? "default"
                                  : "pointer",
                              textDecoration:
                                transcription?.status?.final === AGENDA.PAUSE
                                  ? "none"
                                  : "underline",
                              textDecorationColor:
                                transcription?.status?.final === AGENDA.PAUSE
                                  ? "inherit"
                                  : "white",
                              display: "block",
                            }}
                          >
                            <AppTitleTypography
                              text={
                                transcription?.status?.final === "STOP"
                                  ? transcription?.title + " (summary)"
                                  : transcription?.title + " - paused"
                              }
                              maxLength={
                                AGENDA_DATA.OVERVIEW_AGENDA_NAME_MAX_LENGTH
                              }
                              className="section-title typography-ellipsis"
                              variant="subtitle1"
                            />
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              }
            )}
          </Box>
        </Box>
      </Box>

      <FloatingEditSummaryPopup
        isOpen={editSummModalOpen}
        onClose={handleEditSummModalClose}
        text={editSummary}
        summaryTitle={editSummaryTitle}
        handleUpdateSummary={handleUpdateSummary}
        countryOptions={countryOptions}
      />
      <LoaderWithText
        isOpen={isAgendaBroadcasting}
        onClose={dummyModalCloseTw}
      />
      <LoaderWithText
        isOpen={apiLoadingCount > 0}
        onClose={() => { }}
      />
      {addSpeakerModalOpen && addSpeakerAfterIndex !== null && (
        <AddSpeakerSectionModal
          open={addSpeakerModalOpen}
          onClose={handleCloseAddSpeakerModal}
          onSave={handleAddSpeakerSection}
          availableSpeakerIds={getAvailableSpeakerIds()}
          isLoading={isAddingSpeaker}
        />
      )}
      {editSpeakerModalOpen && editingSpeaker && (
        <EditSpeakerModal
          open={editSpeakerModalOpen}
          onClose={handleCloseEditSpeakerModal}
          onSave={handleEditSpeaker}
          speaker={editingSpeaker}
          isLoading={isEditingSpeaker}
        />
      )}
      <AppDialogBox
        title="Delete Speaker Section"
        isDialogOpen={deleteConfirmOpen}
        handleDialogClose={handleCloseDeleteConfirm}
        dialogButtons={
          <>
            <MuiButton onClick={handleCloseDeleteConfirm} disabled={isDeletingSpeaker}>
              Cancel
            </MuiButton>
            <MuiButton
              onClick={handleDeleteSpeaker}
              variant="contained"
              color="error"
              disabled={isDeletingSpeaker}
            >
              {isDeletingSpeaker ? "Deleting..." : "Delete"}
            </MuiButton>
          </>
        }
      >
        <Typography>
          Are you sure you want to delete this speaker section? This action cannot be undone.
        </Typography>
      </AppDialogBox>
      <AppDialogBox
        title="Delete Intervention Summary"
        isDialogOpen={deleteInterventionSummaryConfirmOpen}
        handleDialogClose={handleCloseDeleteInterventionSummaryConfirm}
        dialogButtons={
          <>
            <MuiButton
              onClick={handleCloseDeleteInterventionSummaryConfirm}
              disabled={isDeletingInterventionSummary}
            >
              No
            </MuiButton>
            <MuiButton
              onClick={handleDeleteInterventionSummary}
              variant="contained"
              color="error"
              disabled={isDeletingInterventionSummary}
            >
              {isDeletingInterventionSummary ? "Deleting..." : "Yes"}
            </MuiButton>
          </>
        }
      >
        <Typography>
          Are you sure you want to delete this intervention summary? This action cannot be undone.
        </Typography>
      </AppDialogBox>
      <AppDialogBox
        title="Connection Lost"
        isDialogOpen={connectionLostOpen}
        handleDialogClose={() => window.location.reload()}
        disableBackdropClick
        dialogButtons={
          <MuiButton
            variant="contained"
            onClick={() => window.location.reload()}
          >
            OK
          </MuiButton>
        }
      >
        <Typography>
          Please refresh the page to continue.
        </Typography>
      </AppDialogBox>
    </Box>
  );
};

export default MainContainer;
